import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  gstAppliesToDocumentFor,
  gstDay,
  isPreGstRegistrationFor,
} from "../../../../shared/utils/gstApplicability";
import { GstPreRegistrationNotice } from "../../../../shared/presentation/components/GstPreRegistrationNotice";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { useLocation, useNavigate } from "react-router-dom";

// UI Components
import { Badge } from "../../../../ui/components/Badge";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { Dialog } from "../../../../ui/components/Dialog";
import { Tooltip } from "../../../../ui/components/Tooltip";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";

// Icons
import {
  FeatherAlertCircle,
  FeatherAlertOctagon,
  FeatherAlertTriangle,
  FeatherCheck,
  FeatherChevronDown,
  FeatherCopy,
  FeatherDownload,
  FeatherEdit2,
  FeatherFile,
  FeatherInfo,
  FeatherLock,
  FeatherMessageCircle,
  FeatherPlus,
  FeatherUserPen,
  FeatherX,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";

// Feature Components
import SearchCustomerModal from "../components/SearchCustomerModal";
import AIDetectionOverviewInvoices from "../components/AIDetectionOverviewInvoices";
import CustomerRerunConfirmModal from "../components/CustomerRerunConfirmModal";
import EditLineItemForInvoiceComponent from "../components/EditLineItemForInvoiceComponent";
import { getInvoiceListStatus } from "../utils/invoiceListStatus";
import { useInvoiceQueries } from "../hooks";

// Shared Components
import QueryRaiseModal from "../../../../shared/queries/presentation/components/QueryRaiseModal";
import QueryDetailModal from "../../../../shared/queries/presentation/components/QueryDetailModal";
import QueriesListForDocument from "../../../../shared/queries/presentation/components/QueriesListForDocument";
import DocumentViewer from "../../../../components/DocumentViewer";
import ActivityLogComponent from "../../../../components/ActivityLogComponent";
import {
  BasicInfoEditableField,
  DatesEditableField,
  TaxInfoEditableField,
  DocumentMetadataEditableField,
  AmountSummaryEditableField,
  ReviewPendingInlineValue,
} from "../../../../shared/presentation/components/document-review";
import {
  ErrorBanner,
  useToast,
  GLClassificationModal,
  GLClassificationLineItem,
  ApplyGLAsDefaultModal,
  ApplyGLAsDefaultLineItem,
} from "../../../../shared/presentation/components";
import UpdateGLDialog from "../../../../shared/presentation/components/UpdateGLDialog";
import { useErrorHandling } from "../../../../shared/presentation/hooks";

// API & Data
import { getCompany } from "../../../../shared/data/storage/authStorage";
import {
  getSignedUrlForDocument,
  SemanticOverrideRequest,
  createSemanticOverride,
} from "../../../../services/api";
import { formatDetectedLineItemGstDisplay } from "../../../../shared/utils/lineItemGstDisplay";
import {
  pushManualInvoiceDraft,
  cancelWriteOffInvoice,
  rerunCustomerChange,
  getRecomputeStatus,
} from "../../data/api/invoicesApi";
import { buildManualInvoiceJson } from "../../../../shared/utils/buildManualDocumentJson";

// Invoices Feature Hooks
import { useInvoiceOperations } from "../hooks/useInvoiceOperations";
import { useInvoiceProcessing } from "../hooks/useInvoiceProcessing";

// Clean Architecture: Queries hook imported via useInvoiceReview
import {
  InvoiceDataEntity,
  LineItemEntity,
} from "../../domain/entities/InvoiceEntity";

// Hooks
import { useTaxes } from "../../../../shared/presentation/hooks/useTaxes";
import { useGSTDetermination } from "../../../../shared/presentation/hooks/useGSTDetermination";
import { useProposedDecisions } from "../../../../shared/presentation/hooks/useProposedDecisions";
import { useTcsRecommendation } from "../../../../shared/presentation/hooks/useTcsRecommendation";

// Types (Query type no longer needed - using QueryEntity from clean architecture)

// Utils
import {
  getStateAbbreviation,
  getAccountNameLeaf,
  getCountryAbbreviation,
} from "../../../../utils";
// Permissions
import { useDocumentReviewData } from "../../../../shared/presentation/hooks";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { InvoiceBillReviewChangesModal } from "../../../../shared/presentation/components/InvoiceBillReviewChangesModal";
import {
  ReviewStatusBanner,
  isReviewStatusBannerVisibleForUser,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
} from "../../../../shared/presentation/components";
import { useAuth } from "../../../auth/presentation/hooks/useAuth";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
  ApprovalAuditPayload,
} from "../../../../shared/utils/reviewWorkflow";
import { dateToMonthYear } from "../../../../shared/utils/date";
import {
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../../../../shared/presentation/hooks";
import { useEntityReviewStatus } from "../../../../shared/presentation/hooks/useEntityReviewStatus";
import { GetInvoiceByIdUseCase } from "../../domain/use-cases/invoices/GetInvoiceByIdUseCase";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";
import {
  buildInvoicePendingReviewDisplayMapsFromChangeDetails,
  INVOICE_HEADER_TRACK_KEY_SET,
  INVOICE_LINE_FIELDS,
  INVOICE_LINE_TRACK_KEY_SET,
  isInvoiceTrackedFieldChangeRowAllowed,
  shortenTrackedPartyChangeNewValue,
} from "../../../../shared/utils/review";
import {
  getDeletedFromBackendSuccessMessage,
  getPushPermissionDeniedMessage,
  getReviewRequestPushReason,
  getReviewRequestUpdateAfterPostReason,
  getStoredAccountingBackend,
  getUpdateDocumentAfterPushPermissionDeniedMessage,
} from "../../../../config/accounting";
import {
  applyExchangeRateForAccountingPayload,
  formatExchangeRateForBasicInfoField,
  isExchangeRateMissingForTallyPost,
  needsZohoExchangeRateFetch,
} from "../../../../shared/utils/documentExchangeRate";
import { buildValidationSnapshotForReview } from "../../../../shared/utils/review/validationSnapshot";
import { useZohoDocumentExchangeRate } from "../../../../shared/hooks/useZohoDocumentExchangeRate";

const getSupplyLocationCode = (
  state: string | null | undefined,
  country: string | null | undefined,
): string => {
  const stateValue = String(state ?? "").trim();
  return stateValue
    ? getStateAbbreviation(stateValue)
    : getCountryAbbreviation(country);
};

interface InvoiceReviewPageProps {}

// Create repository and use case instances for document fetching
const invoiceRepository = new InvoiceRepositoryImpl();
const getInvoiceByIdUseCase = new GetInvoiceByIdUseCase(invoiceRepository);

type InlineGlSuggestion = {
  rank?: number;
  gl_id?: string;
  gl_name: string;
  account_id: string;
  source_layer?: string;
  confidence?: number | null;
  reason?: string;
};

// Inline GL picker for the Credit A/c cell (invoices, 2026-06-26). Mirror of the
// bills Debit A/c picker: for LLM-classified lines the AI's auto-pick is hidden
// and the reviewer sees an empty "Select account" box; clicking it opens the
// ranked "AI Suggested" list. Rendered in a portal (SubframeCore.Popover) so it
// escapes the cell's overflow-hidden. Picking does NOT auto-close, so onSelect
// closes it explicitly via the parent's open state.
function InlineGlAccountPicker({
  open,
  onOpenChange,
  suggestions,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestions: InlineGlSuggestion[];
  onSelect: (s: InlineGlSuggestion) => void;
}) {
  return (
    <SubframeCore.Popover.Root open={open} onOpenChange={onOpenChange}>
      <SubframeCore.Popover.Trigger asChild={true}>
        {/* Empty AI picker box with an animated blue shimmer ring (.gl-ai-ring in
            index.css) signalling an AI suggestion awaiting the reviewer's pick.
            text-warning-700 = blue in this theme (brand ramp is grayscale; the
            warning ramp is sky-blue). */}
        <button
          type="button"
          className="gl-ai-ring flex w-full items-center gap-1 px-2 py-1 text-left"
        >
          <span className="truncate text-caption-bold font-caption-bold text-warning-700">
            Select account
          </span>
          <FeatherChevronDown className="ml-auto flex-none text-caption font-caption text-warning-700" />
        </button>
      </SubframeCore.Popover.Trigger>
      <SubframeCore.Popover.Portal>
        <SubframeCore.Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50"
        >
          <div className="flex w-80 flex-col gap-1.5 rounded-md border border-solid border-neutral-200 bg-white px-3 py-2 shadow-lg">
            <span className="text-caption-bold font-caption-bold text-subtext-color">
              AI Suggested
            </span>
            {suggestions.map((suggestion, idx) => {
              const pct =
                suggestion.confidence != null
                  ? Math.round(suggestion.confidence * 100)
                  : null;
              return (
                <button
                  type="button"
                  key={`${suggestion.account_id}-${idx}`}
                  onClick={() => onSelect(suggestion)}
                  className="flex w-full flex-col gap-0.5 rounded-md border border-solid border-transparent px-2.5 py-1.5 text-left transition-colors hover:bg-neutral-100"
                >
                  <div className="flex w-full items-center gap-2">
                    {pct != null && (
                      <span
                        className={`flex-none rounded-full px-1.5 py-0.5 text-caption font-caption ${
                          idx === 0
                            ? "bg-success-100 text-success-800"
                            : "bg-neutral-200 text-subtext-color"
                        }`}
                      >
                        {pct}%
                      </span>
                    )}
                    <span className="min-w-0 truncate text-body-bold font-body-bold text-default-font">
                      {getAccountNameLeaf(suggestion.gl_name)}
                    </span>
                  </div>
                  {suggestion.reason && (
                    <span className="text-caption font-caption text-subtext-color leading-snug">
                      {suggestion.reason}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </SubframeCore.Popover.Content>
      </SubframeCore.Popover.Portal>
    </SubframeCore.Popover.Root>
  );
}

const InvoiceReviewPage: React.FC<InvoiceReviewPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerMatched = location.state?.isCustomerMatched;
  // const isEditMode = location.state?.isEdit || false;
  const [isSearchCustomerModalOpen, setIsSearchCustomerModalOpen] =
    useState(false);
  const [isQueryRaiseModalOpen, setIsQueryRaiseModalOpen] = useState(false);
  const [customerMatched, setCustomerMatched] = useState(isCustomerMatched);

  // Extract data from navigation state
  // If location.state is lost (e.g., after navigate(-1)), try to restore from sessionStorage or query params
  const getDocumentIdFromStateOrStorage = (): string | null => {
    if (location.state?.documentId) {
      // Store in sessionStorage for recovery
      sessionStorage.setItem(
        "invoiceReviewDocumentId",
        location.state.documentId,
      );
      return location.state.documentId;
    }
    // Try to get from query parameters (for new tab opens)
    const searchParams = new URLSearchParams(location.search);
    const queryDocumentId = searchParams.get("documentId");
    if (queryDocumentId) {
      sessionStorage.setItem("invoiceReviewDocumentId", queryDocumentId);
      return queryDocumentId;
    }
    // Try to recover from sessionStorage
    const stored = sessionStorage.getItem("invoiceReviewDocumentId");
    return stored || null;
  };

  const documentId = getDocumentIdFromStateOrStorage();
  const pendingReviewIdFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("reviewId");
  }, [location.search]);
  const jobId =
    location.state?.jobId ||
    (() => {
      const stored = sessionStorage.getItem("invoiceReviewJobId");
      return stored || null;
    })();
  // const initialInvoiceJson: InvoiceDataEntity =
  //   location.state?.invoiceJson || null;
  const initialInvoiceJson: InvoiceDataEntity | null = null;
  const initialInvoiceDocument = location.state?.invoiceDocument || null;

  const initialSignedUrl: string | null = location.state?.signedUrl || null;
  const initialFileName = location.state?.fileName || null;

  // Store jobId in sessionStorage if available
  useEffect(() => {
    if (location.state?.jobId) {
      sessionStorage.setItem("invoiceReviewJobId", location.state.jobId);
    }
  }, [location.state?.jobId]);

  // Fetch proposed decisions using custom hook
  const { proposedDecisions } = useProposedDecisions(jobId);

  // Fetch TCS recommendation once when user lands on review screen (by job_id)
  const { tcsRecommendation } = useTcsRecommendation(jobId);

  // Track if user has changed TCS from dropdown so we hide the AI recommendation info icon
  const [userHasChangedTcs, setUserHasChangedTcs] = useState(false);

  // ============================================
  // NEW: Use unified hook to fetch all data (permissions + document + review statuses)
  // ============================================
  // Memoize functions to prevent infinite loops
  const fetchInvoiceDocument = useCallback(async (id: string) => {
    return await getInvoiceByIdUseCase.execute(parseInt(id));
  }, []);

  const checkInvoicePostedToZoho = useCallback((doc: any) => {
    return getInvoiceListStatus(doc) === "posted";
  }, []);

  const reviewData = useDocumentReviewData({
    moduleCode: MODULE_CODES.INVOICE,
    entityType: "invoice",
    documentId,
    fetchDocument: fetchInvoiceDocument,
    isPostedToZoho: checkInvoicePostedToZoho,
    initialDocument: initialInvoiceDocument,
  });

  // Extract values from reviewData (includes canCreate, needsReviewForCreate for manual-draft create flow)
  const {
    isDataLoading,
    document: invoiceDocument,
    isPostedToZoho,
    canEdit,
    canUpdateAfterPush,
    canPushToZoho,
    canCreate,
    canReject,
    canMarkAsDuplicate,
    canDelete,
    canCancelWriteOff,
    needsReviewForEdit,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    needsReviewForCreate,
    needsReviewForOverrideGLPermanent,
    needsReviewForReject,
    needsReviewForDuplicate,
    needsReviewForDelete,
    needsReviewForCancelWriteOff,
    hasApprovedUpdateAfterPushReview,
    canSaveAndGoBack,
    canApprove,
    error: reviewDataError,
  } = reviewData;

  // Get invoice document details (for signedUrl, fileName, etc.)
  const invoiceDoc = invoiceDocument as any;
  const signedUrl = invoiceDoc?.signed_url || initialSignedUrl;
  const fileName = invoiceDoc?.original_filename || initialFileName;
  const fetchedInvoiceJson = invoiceDoc?.invoice_json ?? null;
  const isManualSource = invoiceDoc?.source === "manual";
  const isZohoSource = invoiceDoc?.source === "zoho";
  const isTallySource = invoiceDoc?.source === "tally";
  const attachmentUrls =
    (invoiceDoc?.attachment_urls as Array<{
      signed_url: string;
      filename: string;
      file_size?: number | null;
    }>) || [];

  // Working copy (modified by user edits)
  const [invoiceJson, setInvoiceJson] = useState<InvoiceDataEntity | null>(
    initialInvoiceJson,
  );

  // Ref to track latest invoiceJson for review requests (to avoid stale state)
  const invoiceJsonRef = useRef<InvoiceDataEntity | null>(initialInvoiceJson);
  useEffect(() => {
    invoiceJsonRef.current = invoiceJson;
  }, [invoiceJson]);

  // ============================================
  // CHANGE TRACKING: Track all changes made by user
  // ============================================
  // Store the original document when first loaded (for comparison)
  const [originalInvoiceJson, setOriginalInvoiceJson] =
    useState<InvoiceDataEntity | null>(initialInvoiceJson);

  /**
   * Baseline snapshot for system-driven updates applied during validation.
   * Used to avoid tracking tax/GL fields that the system auto-populates in `handleValidationComplete`.
   * This should only be updated from validation flows, not from user edits.
   */
  const validationBaselineLineItemsRef = useRef<any[] | null>(null);

  /**
   * Lock the baseline after the first validation completes.
   * Subsequent validations (triggered after edits) must NOT overwrite the baseline,
   * otherwise previously-tracked user edits can disappear from the changes summary.
   */
  const validationBaselineLockedRef = useRef(false);

  /**
   * After the first `handleValidationComplete` run, allow line-item field tracking.
   * Until then, `updateLineItems` still updates JSON/totals but does not mutate `trackedChanges`
   * (avoids false line diffs before validation has normalized taxes).
   */
  const lineItemTrackingAfterValidationRef = useRef(false);

  // Track all changes made by the user (accumulated as they edit)
  const [trackedChanges, setTrackedChanges] = useState<
    Array<{
      field: string;
      old: string;
      new: string;
    }>
  >([]);

  // Helper function to format values for change tracking
  // Handles empty strings, null, undefined, and numbers (including 0)
  // For tax_name: empty → "No Tax", otherwise uppercase
  const formatValueForTracking = (value: any, fieldKey?: string): string => {
    const isEmpty =
      value === null ||
      value === undefined ||
      value === "" ||
      value === "empty";
    if (fieldKey === "tax_name") {
      if (isEmpty) return "No Tax";
      return String(value).toUpperCase();
    }
    if (isEmpty) return "empty";
    // Handle objects (arrays, objects) - stringify them
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    // Handle numbers (including 0) - convert to string
    if (typeof value === "number") {
      return String(value);
    }
    return String(value);
  };

  // Helper function to format field names for display
  // Removes underscores, capitalizes first letter of each word
  const formatFieldName = (fieldName: string): string => {
    const formatted = fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    // gst_no on invoice is company/vendor GST (not customer) - label as Vendor GSTIN
    if (
      formatted.toLowerCase() === "gst no" ||
      formatted.toLowerCase() === "gstno"
    ) {
      return "Vendor GSTIN";
    }

    // Replace "Customer Gstin" with "Customer GSTIN" for invoices
    if (
      formatted.toLowerCase() === "customer gstin" ||
      formatted.toLowerCase() === "customer_gstin"
    ) {
      return "Customer GSTIN";
    }

    return formatted;
  };

  const seedTrackedUserLineItemChangesAfterValidation = useCallback(
    (items: any[]) => {
      if (!originalInvoiceJson?.line_items || !Array.isArray(items)) return;
      const userEditableLineKeys = new Set([
        "description",
        "hsn_or_sac",
        "quantity",
        "rate",
        "account_name",
      ]);

      setTrackedChanges((prevTrackedChanges) => {
        const updatedChanges = [...prevTrackedChanges];
        const changesToTrack: Array<{
          field: string;
          old: string;
          new: string;
        }> = [];

        items.forEach((item, index) => {
          const originalItem = originalInvoiceJson.line_items?.[index];
          if (!originalItem) return;

          userEditableLineKeys.forEach((lineFieldKey) => {
            const formattedFieldName = formatFieldName(lineFieldKey);
            const field = `Line Item ${index + 1}: ${formattedFieldName}`;
            const oldFieldValue = (originalItem as any)[lineFieldKey];
            const newFieldValue = (item as any)[lineFieldKey];

            if (oldFieldValue == null && newFieldValue == null) return;

            const existingIndex = updatedChanges.findIndex(
              (change) => change.field === field,
            );

            if (
              JSON.stringify(oldFieldValue) === JSON.stringify(newFieldValue)
            ) {
              if (existingIndex >= 0) updatedChanges.splice(existingIndex, 1);
              return;
            }

            const change = {
              field,
              old: formatValueForTracking(oldFieldValue, lineFieldKey),
              new: formatValueForTracking(newFieldValue, lineFieldKey),
            };

            if (existingIndex >= 0) {
              updatedChanges[existingIndex] = change;
            } else {
              changesToTrack.push(change);
            }
          });
        });

        return [...updatedChanges, ...changesToTrack].filter((change) =>
          isInvoiceTrackedFieldChangeRowAllowed(change.field),
        );
      });
    },
    [originalInvoiceJson],
  );

  // Initialize invoiceJson from fetched data (when API fetch happens)
  useEffect(() => {
    // if (fetchedInvoiceJson && !initialInvoiceJson) {
    //   setInvoiceJson(fetchedInvoiceJson);
    // }
    if (fetchedInvoiceJson) {
      setInvoiceJson((prevInvoiceJson) => {
        // If this is the same invoice (same invoice_number), check if data actually changed
        if (
          prevInvoiceJson &&
          prevInvoiceJson.invoice_number === fetchedInvoiceJson.invoice_number
        ) {
          // Compare key customer-related fields to detect actual changes
          // This prevents infinite loops when navigating back from edit pages
          const customerFieldsChanged =
            prevInvoiceJson.customer_id !== fetchedInvoiceJson.customer_id ||
            prevInvoiceJson.customer_name !==
              fetchedInvoiceJson.customer_name ||
            prevInvoiceJson.customer_gstin !==
              fetchedInvoiceJson.customer_gstin ||
            prevInvoiceJson.customer_address !==
              fetchedInvoiceJson.customer_address ||
            prevInvoiceJson.gst_treatment !==
              fetchedInvoiceJson.gst_treatment ||
            prevInvoiceJson.payment_terms !==
              fetchedInvoiceJson.payment_terms ||
            prevInvoiceJson.place_of_supply !==
              fetchedInvoiceJson.place_of_supply ||
            prevInvoiceJson.destination_of_supply !==
              fetchedInvoiceJson.destination_of_supply;

          // If no customer fields changed, preserve existing invoiceJson to prevent loops
          // Only update if customer-related data actually changed
          if (!customerFieldsChanged) {
            // Still preserve line_items if fetched data doesn't have them
            if (
              prevInvoiceJson.line_items &&
              prevInvoiceJson.line_items.length > 0 &&
              (!fetchedInvoiceJson.line_items ||
                fetchedInvoiceJson.line_items.length === 0)
            ) {
              // No changes needed, return existing state
              return prevInvoiceJson;
            }
            // If line items exist in fetched data, check if they're different
            const lineItemsChanged =
              JSON.stringify(prevInvoiceJson.line_items) !==
              JSON.stringify(fetchedInvoiceJson.line_items);
            if (!lineItemsChanged) {
              // No changes at all, return existing state to prevent loop
              return prevInvoiceJson;
            }
          }

          // If this is the same invoice (same invoice_number), preserve existing line_items
          // This prevents losing line_items when navigating back from EditCustomerPage
          if (
            prevInvoiceJson.line_items &&
            prevInvoiceJson.line_items.length > 0 &&
            (!fetchedInvoiceJson.line_items ||
              fetchedInvoiceJson.line_items.length === 0)
          ) {
            // Preserve existing line_items if fetched data doesn't have them
            return {
              ...fetchedInvoiceJson,
              line_items: prevInvoiceJson.line_items,
            };
          }
        }
        // Otherwise, use fetched data as-is

        return fetchedInvoiceJson;
      });

      // Store original document for change tracking (only if not already set or document changed)
      if (
        !originalInvoiceJson ||
        originalInvoiceJson.invoice_number !== fetchedInvoiceJson.invoice_number
      ) {
        setOriginalInvoiceJson(fetchedInvoiceJson);
        // Reset tracked changes when new document is loaded
        setTrackedChanges([]);
        validationBaselineLineItemsRef.current = null;
        validationBaselineLockedRef.current = false;

        lineItemTrackingAfterValidationRef.current = false;
      }
    }
  }, [fetchedInvoiceJson, originalInvoiceJson]);

  // Check if customer is already matched based on customer_id in invoiceJson
  useEffect(() => {
    if (invoiceJson?.customer_id && invoiceJson.customer_id !== "") {
      setCustomerMatched(true);
    }
  }, [invoiceJson?.customer_id, invoiceJson?.customer_name]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedDataRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  // Activity log refresh key - increment this to trigger activity log refresh
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);

  // Error handling with new hook
  const {
    errors,
    setFieldError,
    setFieldErrors,
    setApiErrors,
    clearErrors,
    clearFieldError,
  } = useErrorHandling();

  // Toast notifications
  const { showSuccess, showError, showWarning } = useToast();

  // Closed month confirm modal (when non-Admin/CFO tries to save or approve in closed month)
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);
  const [pendingClosedMonthSource, setPendingClosedMonthSource] = useState<
    "save" | "approve" | null
  >(null);

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingReviewAction, setPendingReviewAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
    disableSubmit?: boolean;
    actionCode?: string;
    requiresReview?: boolean;
  } | null>(null);

  // Track pending GL overrides that need review (array to support multiple line items)
  const [pendingGLOverrideReview, setPendingGLOverrideReview] = useState<
    Array<{
      lineItemIndex: number;
      glData: {
        old_account_id: string;
        old_account_name: string;
        new_account_id: string;
        new_account_name: string;
      };
      changeScope: "always" | "once";
      reason: string;
      overrideData: SemanticOverrideRequest;
    }>
  >([]);

  // Review status (for banner display)
  const { review } = useEntityReviewStatus(
    documentId ? "invoice" : null,
    documentId ? parseInt(documentId) : null,
  );
  const pendingReviewDisplayChangeDetails =
    pendingReviewIdFromUrl &&
    review?.status === "pending" &&
    String(review.id) === pendingReviewIdFromUrl
      ? review.change_details
      : null;
  const { user } = useAuth();
  const reviewBannerCurrentUserId = user?.id ? parseInt(user.id, 10) : null;

  // Check if review is pending (blocks all actions)
  const isReviewPending = review?.status === "pending";

  useEffect(() => {
    const reviewRawJson = pendingReviewDisplayChangeDetails?.raw_json;
    if (!reviewRawJson || typeof reviewRawJson !== "object") return;

    setInvoiceJson(reviewRawJson);
    if (Array.isArray(reviewRawJson.line_items)) {
      setLineItems([...reviewRawJson.line_items]);
    }
  }, [pendingReviewDisplayChangeDetails]);

  // Check if user has edit permission (edit or update_after_push based on document status)
  const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;

  // Closed month: derive month from invoice date and check if closed
  const invoiceDate = invoiceJson?.date ?? fetchedInvoiceJson?.date ?? "";
  const monthForClosedCheck = invoiceDate ? dateToMonthYear(invoiceDate) : "";
  const { isClosed: isMonthClosed } = useIsMonthClosed(
    monthForClosedCheck || undefined,
  );
  const isAdminOrCFO = useIsAdminOrCFO();

  // Disable all editable fields if review is pending OR user doesn't have update permissions
  const shouldDisableEditableFields = isReviewPending || !hasEditPermission;

  // State for showing approved banner temporarily (auto-dismisses after 40 seconds)
  const [showApprovedBanner, setShowApprovedBanner] = useState(false);
  const approvedBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // AI detection loading: gate Approve/Reject/Duplicate until AIDetectionOverview finishes
  const [aiDetectionLoading, setAiDetectionLoading] = useState(false);

  // Local write-off status override (set to 'none' after successful cancel so we don't refetch)
  const [localWriteOffStatus, setLocalWriteOffStatus] = useState<
    "written_off" | "none" | null
  >(null);
  const [cancelWriteOffLoading, setCancelWriteOffLoading] = useState(false);

  // Written-off: use local override after cancel so we don't need to refetch
  const isWrittenOff =
    (localWriteOffStatus ?? invoiceDocument?.write_off_status) ===
    "written_off";

  // Use review status from reviewData hook
  const isCheckingReview = reviewData.isReviewDataLoading;

  // Show approved banner when review is approved (on mount or when review changes to approved)
  useEffect(() => {
    if (review?.status === "approved") {
      setShowApprovedBanner(true);

      // Clear any existing timeout
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }

      // Auto-dismiss after 40 seconds
      // approvedBannerTimeoutRef.current = setTimeout(() => {
      //   setShowApprovedBanner(false);
      // }, 40000);
    } else {
      setShowApprovedBanner(false);
    }

    // Cleanup timeout on unmount
    // return () => {
    //   if (approvedBannerTimeoutRef.current) {
    //     clearTimeout(approvedBannerTimeoutRef.current);
    //   }
    // };
  }, [review?.status, review?.id]);

  // Permission flags are now computed in the hook
  // canActuallyEdit, canSaveAndGoBack, canApprove, canEditLineItem are already computed

  // Invoice operations hooks
  const {
    createZohoInvoice,
    updateZohoInvoice,
    updateInvoiceJson: updateInvoiceJsonHook,
    rejectInvoice: rejectInvoiceHook,
    markAsDuplicate: markAsDuplicateHook,
    deletePostedInvoice: deletePostedInvoiceHook,
    error: invoiceOperationsError,
  } = useInvoiceOperations();

  const {
    processInvoiceJson: processInvoiceJsonHook,
    error: invoiceProcessingError,
  } = useInvoiceProcessing();

  // Display errors from hooks in ErrorBanner
  useEffect(() => {
    if (invoiceOperationsError) {
      setApiErrors(new Error(invoiceOperationsError));
    }
  }, [invoiceOperationsError, setApiErrors]);

  useEffect(() => {
    if (invoiceProcessingError) {
      setApiErrors(new Error(invoiceProcessingError));
    }
  }, [invoiceProcessingError, setApiErrors]);

  // Debounced save function with retry logic
  const debouncedSave = useCallback(
    async (invoiceData: InvoiceDataEntity, isRetry = false) => {
      if (!documentId) {
        console.error("No documentId to save");
        return;
      }

      // CRITICAL: Don't save if data is still loading
      if (isDataLoading) {
        console.log("Data still loading, skipping save");
        return;
      }

      // Closed month: non-Admin/CFO cannot save directly; show confirm modal to submit for review
      if (isMonthClosed && !isAdminOrCFO) {
        setPendingClosedMonthSource("save");
        setShowClosedMonthConfirmModal(true);
        return;
      }

      // CRITICAL: Check permissions before saving
      // For documents NOT pushed to Zoho: Check can_update permission
      // For documents pushed to Zoho: Check can_update_after_push permission AND review status
      if (isPostedToZoho) {
        // Document is pushed to Zoho - need update_after_push permission
        if (!canUpdateAfterPush) {
          console.log("No permission to update after push, skipping save");
          return;
        }
        // If review is required, check if we have approved review
        if (
          needsReviewForUpdateAfterPush &&
          hasApprovedUpdateAfterPushReview !== true
        ) {
          console.log("Review required but not approved, skipping save");
          return;
        }
      } else {
        // Document is NOT pushed to Zoho - need edit permission
        if (!canEdit) {
          console.log("No permission to edit, skipping save");
          return;
        }
      }

      // Check if data has actually changed to prevent unnecessary API calls
      const currentDataString = JSON.stringify(invoiceData);
      if (lastSavedDataRef.current === currentDataString && !isRetry) {
        return;
      }

      try {
        setIsAutoSaving(true);
        setSaveError(null);

        await updateInvoiceJsonHook(parseInt(documentId), invoiceData);
        setLastSaved(new Date());
        lastSavedDataRef.current = currentDataString;
        retryCountRef.current = 0; // Reset retry count on success

        // Refresh activity log after successful save
        setActivityLogRefreshKey((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to save invoice JSON:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save changes";
        setSaveError(errorMessage);

        // Retry logic for network errors
        if (retryCountRef.current < maxRetries && !isRetry) {
          retryCountRef.current++;

          setTimeout(() => {
            debouncedSave(invoiceData, true);
          }, 2000 * retryCountRef.current); // Exponential backoff
        }
      } finally {
        setIsAutoSaving(false);
      }
    },
    [
      documentId,
      maxRetries,
      updateInvoiceJsonHook,
      isDataLoading,
      isMonthClosed,
      isAdminOrCFO,
      isPostedToZoho,
      canUpdateAfterPush,
      canEdit,
      needsReviewForUpdateAfterPush,
      hasApprovedUpdateAfterPushReview,
    ],
  );
  // Function to trigger debounced save
  const triggerAutoSave = useCallback(
    (invoiceData: InvoiceDataEntity) => {
      debouncedSave(invoiceData);
    },
    [debouncedSave],
  );

  // Function to update invoiceJson data
  // ZOUR-65: which of our GST registrations issued this invoice.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
  } = useActiveLocations();

  const updateInvoiceJson = useCallback(
    (updates: Partial<InvoiceDataEntity>) => {
      // CRITICAL: Don't allow updates if data is still loading
      if (isDataLoading) {
        return;
      }

      // Check if user has permission to edit (edit or update_after_push)
      const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;
      if (!hasEditPermission) {
        return;
      }

      setInvoiceJson((prev: InvoiceDataEntity | null) => {
        // If prev is null but we have fetchedInvoiceJson, use it as base
        // This prevents clearing line_items when auto-select runs before invoiceJson is initialized
        // Critical fix for production race condition with overseas customers
        if (!prev) {
          if (fetchedInvoiceJson) {
            const updated = { ...fetchedInvoiceJson, ...updates };

            return updated;
          }
          return null;
        }
        const updated = { ...prev, ...updates };

        // Track changes by comparing with original document
        // Use functional update to always work with latest trackedChanges state
        if (originalInvoiceJson) {
          setTrackedChanges((prevTrackedChanges) => {
            const changesToTrack: Array<{
              field: string;
              old: string;
              new: string;
            }> = [];
            const updatedChanges = [...prevTrackedChanges];

            // Customer-related fields that should be removed when customer_id changes
            // These are fields that get updated automatically when customer changes
            const customerAutoUpdateFields = [
              "customer_id",
              "customer_name",
              "customer_gstin",
              "customer_address",
              "payment_terms",
              "place_of_supply",
              "destination_of_supply",
            ];

            // Check if customer_id is being changed
            const isCustomerIdChanging =
              updates.customer_id !== undefined &&
              originalInvoiceJson.customer_id !== updates.customer_id;

            // If customer_id is changing, remove all customer-related tracked changes
            if (isCustomerIdChanging) {
              // Remove all customer auto-update fields from tracked changes
              customerAutoUpdateFields.forEach((field) => {
                const index = updatedChanges.findIndex(
                  (change) => change.field === field,
                );
                if (index >= 0) {
                  updatedChanges.splice(index, 1);
                }
              });

              // Remove existing "Customer" change if it exists (prevent duplicates)
              const existingCustomerIndex = updatedChanges.findIndex(
                (change) => change.field === "Customer",
              );
              if (existingCustomerIndex >= 0) {
                updatedChanges.splice(existingCustomerIndex, 1);
              }

              // Track customer change (for display)
              const oldCustomerId = originalInvoiceJson.customer_id;
              const oldCustomerName =
                originalInvoiceJson.customer_name || "Unknown";
              // Get new customer name from updates or from the merged updated object
              const newCustomerName =
                (updates.customer_name as string) ||
                updated.customer_name ||
                "Unknown";

              // Check if customer is being created (old is empty/null)
              if (
                !oldCustomerId ||
                oldCustomerId === "" ||
                oldCustomerId === "empty"
              ) {
                changesToTrack.push({
                  field: "Customer",
                  old: "empty",
                  new: `${newCustomerName}`,
                });
              } else {
                changesToTrack.push({
                  field: "Customer",
                  old: oldCustomerName,
                  new: newCustomerName,
                });
              }
            }

            // Do not track Customer GSTIN / Vendor GSTIN changes (excluded from field_changes for review)
            const gstinKeysToIgnore = [
              "customer_gstin",
              "vendor_gstin",
              "gst_no",
            ];

            // Process all updates
            Object.keys(updates).forEach((key) => {
              // Skip line_items - handled separately in updateLineItems
              if (key === "line_items") {
                return;
              }

              // Skip GSTIN fields - do not show in Changes Requested
              if (gstinKeysToIgnore.includes(key)) {
                return;
              }

              // FBAC: only header keys from the shared invoice allowlist
              if (!INVOICE_HEADER_TRACK_KEY_SET.has(key)) {
                return;
              }

              // Skip customer_id if we already handled it above
              if (isCustomerIdChanging && key === "customer_id") {
                return;
              }

              // Skip customer auto-update fields if customer_id is changing
              // (they get updated automatically when customer changes)
              if (
                isCustomerIdChanging &&
                customerAutoUpdateFields.includes(key)
              ) {
                return;
              }

              const oldValue = (originalInvoiceJson as any)[key];
              const newValue = (updates as any)[key];

              // Only track if value actually changed from original
              if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                // Format field name for display
                const formattedFieldName = formatFieldName(key);

                // Check if this field is already tracked
                const existingChangeIndex = updatedChanges.findIndex(
                  (change) => change.field === formattedFieldName,
                );

                if (existingChangeIndex >= 0) {
                  // Update existing tracked change in place
                  updatedChanges[existingChangeIndex] = {
                    field: formattedFieldName,
                    old: formatValueForTracking(oldValue),
                    new: formatValueForTracking(newValue),
                  };
                } else {
                  // Add new tracked change
                  changesToTrack.push({
                    field: formattedFieldName,
                    old: formatValueForTracking(oldValue),
                    new: formatValueForTracking(newValue),
                  });
                }
              } else {
                // If value matches original, remove it from tracked changes (user reverted)
                const formattedFieldName = formatFieldName(key);
                const existingChangeIndex = updatedChanges.findIndex(
                  (change) => change.field === formattedFieldName,
                );
                if (existingChangeIndex >= 0) {
                  updatedChanges.splice(existingChangeIndex, 1);
                }
              }
            });

            // Exclude any GSTIN changes that might still be in updatedChanges (from previous state)
            const combined = [...updatedChanges, ...changesToTrack];
            const gstinDisplayNames = ["Customer GSTIN", "Vendor GSTIN"];
            const filtered = combined.filter(
              (c) => !gstinDisplayNames.includes(c.field),
            );

            // FBAC: drop any non-allowlisted rows (e.g. legacy state)
            return filtered.filter((c) =>
              isInvoiceTrackedFieldChangeRowAllowed(c.field),
            );
          });
        }

        // CRITICAL: Never trigger auto-save for posted documents
        // Only trigger auto-save if we have permission (for non-posted documents)
        // AND if there's no pending GL override review (stop auto-save when GL override needs review)
        // if (!isPostedToZoho && canUpdate) {
        //   triggerAutoSave(updated);
        // }
        return updated;
      });
    },
    [
      // triggerAutoSave,
      isPostedToZoho,
      canEdit,
      canUpdateAfterPush,
      isDataLoading,
      originalInvoiceJson,
      fetchedInvoiceJson, // Add fetchedInvoiceJson to use as fallback when prev is null
      // Note: trackedChanges removed from deps - using functional updates instead
    ],
  );

  // Function to update line items
  const updateLineItems = (updatedLineItems: any[]) => {
    // CRITICAL: Don't allow updates if data is still loading
    if (isDataLoading) {
      return;
    }

    // Check if user has permission to edit (edit or update_after_push)
    const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;
    if (!hasEditPermission) {
      return;
    }

    setInvoiceJson((prev) => {
      if (!prev) return null;

      // Recalculate totals using rate * quantity
      const newSubTotal = updatedLineItems.reduce(
        (sum, item) => sum + (item.rate || 0) * (item.quantity || 0),
        0,
      );
      const newTaxTotal = updatedLineItems.reduce((sum, item) => {
        const taxPercentage = item.tax_percentage || 0;
        const taxAmount =
          (item.rate || 0) * (item.quantity || 0) * (taxPercentage / 100);
        return sum + taxAmount;
      }, 0);
      const newTotal = newSubTotal + discount + newTaxTotal;

      const updated = {
        ...prev,
        line_items: updatedLineItems,
        sub_total: newSubTotal,
        tax_total: newTaxTotal,
        total: newTotal,
        discount: discount,
      };

      // Track line items changes using functional update to avoid duplicates
      if (
        originalInvoiceJson &&
        (lineItemTrackingAfterValidationRef.current || isManualSource)
      ) {
        const originalLineItems = originalInvoiceJson.line_items || [];
        const baselineLineItems = validationBaselineLineItemsRef.current;
        const systemManagedLineKeys = new Set<string>([
          "tax_id",
          "tax_percentage",
          "tax_name",
          "tax_amount",
          "account_id",
          "account_name",
          // Derived from qty × rate × tax; compare from validation baseline so
          // handleValidationComplete recalculations are not tracked as user edits.
          "item_total_inclusive_of_tax",
        ]);

        setTrackedChanges((prevTrackedChanges) => {
          const changesToTrack: Array<{
            field: string;
            old: string;
            new: string;
          }> = [];
          // Remove any existing "Line Items" or line_items[${index}] entries (we don't want raw JSON)
          const updatedChanges = prevTrackedChanges.filter(
            (change) =>
              !change.field.startsWith("line_items[") &&
              change.field !== "Line Items" &&
              !change.field.endsWith("]"),
          );

          // Track changes for each line item
          updatedLineItems.forEach((item, index) => {
            const originalItem = originalLineItems[index];
            const fieldKey = `line_items[${index}]`;

            if (!originalItem) {
              // New line: only allowlisted line fields (shared FBAC module)
              const lineNum = index + 1;
              const lineItemPrefix = `Line Item ${lineNum}`;
              for (let i = updatedChanges.length - 1; i >= 0; i--) {
                if (
                  updatedChanges[i].field === lineItemPrefix ||
                  updatedChanges[i].field.startsWith(lineItemPrefix + ":")
                ) {
                  updatedChanges.splice(i, 1);
                }
              }
              INVOICE_LINE_FIELDS.forEach(({ key: lineFieldKey }) => {
                const value = (item as any)[lineFieldKey];
                if (value === undefined && lineFieldKey !== "tax_name") return;
                const formattedFieldName = formatFieldName(lineFieldKey);
                const displayValue =
                  lineFieldKey === "tax_name" &&
                  (!value || String(value).trim() === "")
                    ? "No Tax"
                    : formatValueForTracking(value, lineFieldKey);
                changesToTrack.push({
                  field: `${lineItemPrefix}: ${formattedFieldName}`,
                  old: "empty",
                  new: displayValue,
                });
              });
            } else if (JSON.stringify(originalItem) !== JSON.stringify(item)) {
              // Line item modified - track only allowlisted fields that changed
              const changedFields: Array<{
                field: string;
                old: string;
                new: string;
              }> = [];

              INVOICE_LINE_TRACK_KEY_SET.forEach((fieldKey) => {
                const baselineItem =
                  Array.isArray(baselineLineItems) &&
                  baselineLineItems[index] != null
                    ? baselineLineItems[index]
                    : null;
                const compareFrom =
                  baselineItem && systemManagedLineKeys.has(fieldKey)
                    ? baselineItem
                    : originalItem;

                const oldHasKey = Object.prototype.hasOwnProperty.call(
                  compareFrom,
                  fieldKey,
                );
                const newHasKey = Object.prototype.hasOwnProperty.call(
                  item as any,
                  fieldKey,
                );
                if (!oldHasKey && !newHasKey) return;

                const oldFieldValue = (compareFrom as any)[fieldKey];
                const newFieldValue = (item as any)[fieldKey];
                if (oldFieldValue == null && newFieldValue == null) return;

                if (
                  JSON.stringify(oldFieldValue) !==
                  JSON.stringify(newFieldValue)
                ) {
                  const formattedFieldName = formatFieldName(fieldKey);
                  changedFields.push({
                    field: `Line Item ${index + 1}: ${formattedFieldName}`,
                    old: formatValueForTracking(oldFieldValue, fieldKey),
                    new: formatValueForTracking(newFieldValue, fieldKey),
                  });
                }
              });

              // If there are changed fields, track them
              if (changedFields.length > 0) {
                // Field-level merge:
                // - Preserve other line-item fields previously tracked.
                // - For the SAME field (e.g. Account Name), always overwrite with the latest value.
                // - Remove only fields that are no longer changed (user reverted).
                const changedFieldNames = new Set(
                  changedFields.map((c) => c.field),
                );

                // Prune stale entries for this line item that are no longer changed
                INVOICE_LINE_TRACK_KEY_SET.forEach((lineFieldKey) => {
                  const formattedFieldName = formatFieldName(lineFieldKey);
                  const label = `Line Item ${index + 1}: ${formattedFieldName}`;
                  if (changedFieldNames.has(label)) return;
                  const existingIndex = updatedChanges.findIndex(
                    (c) => c.field === label,
                  );
                  if (existingIndex >= 0) {
                    updatedChanges.splice(existingIndex, 1);
                  }
                });

                // Upsert changed fields (latest wins)
                changedFields.forEach((change) => {
                  const fieldChangeIndex = updatedChanges.findIndex(
                    (c) => c.field === change.field,
                  );
                  if (fieldChangeIndex >= 0) {
                    updatedChanges[fieldChangeIndex] = change;
                  } else {
                    changesToTrack.push(change);
                  }
                });
              } else {
                // No changes for this line item anymore: remove any previously tracked allowlisted fields.
                INVOICE_LINE_TRACK_KEY_SET.forEach((lineFieldKey) => {
                  const formattedFieldName = formatFieldName(lineFieldKey);
                  const label = `Line Item ${index + 1}: ${formattedFieldName}`;
                  const existingIndex = updatedChanges.findIndex(
                    (c) => c.field === label,
                  );
                  if (existingIndex >= 0) {
                    updatedChanges.splice(existingIndex, 1);
                  }
                });
              }
            } else {
              // Line item matches original - remove from tracked changes if exists
              // Remove both raw format (line_items[index]) and formatted format (Line Items)
              const existingRawIndex = updatedChanges.findIndex(
                (change) => change.field === fieldKey,
              );
              if (existingRawIndex >= 0) {
                updatedChanges.splice(existingRawIndex, 1);
              }
              // Also remove formatted "Line Items" field if it exists
              const existingFormattedIndex = updatedChanges.findIndex(
                (change) => change.field === "Line Items",
              );
              if (existingFormattedIndex >= 0) {
                updatedChanges.splice(existingFormattedIndex, 1);
              }
            }
          });

          // Track removed line items
          if (originalLineItems.length > updatedLineItems.length) {
            for (
              let i = updatedLineItems.length;
              i < originalLineItems.length;
              i++
            ) {
              const itemName =
                originalLineItems[i].name ||
                originalLineItems[i].description ||
                "Item";
              // Track as readable format only
              changesToTrack.push({
                field: `Line Item ${i + 1}`,
                old: itemName,
                new: "Removed",
              });
              // DO NOT track raw JSON - we don't need it in change_details
            }
          }
          return [...updatedChanges, ...changesToTrack].filter((c) =>
            isInvoiceTrackedFieldChangeRowAllowed(c.field),
          );
        });
      }

      // CRITICAL: Never trigger auto-save for posted documents
      // Only trigger auto-save if not Posted to Zoho (for non-posted documents)
      // if (!isPostedToZoho && canUpdate) {
      //   triggerAutoSave(updated);
      // }

      return updated;
    });
  };

  // Handle navigation back
  const handleGoBack = useCallback(() => {
    // Clear sessionStorage before navigating away
    sessionStorage.removeItem("autoSelectCustomer");
    navigate("/dashboard/invoices");
  }, [navigate]);

  // Helper function to get field value with fallback
  const getFieldValue = (
    value: any,
    fallback: string = "Not provided",
  ): string => {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }
    return String(value);
  };

  // Helper function to format amount with proper rounding
  const formatAmount = (value: any, fallback: string = "0"): string => {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return fallback;
    }
    // return Math.round(numValue).toString();
    return numValue.toFixed(2);
  };

  const parseTaxRateFromName = (taxName: string): number | null => {
    const match = taxName?.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  };

  const formatRate = (rate: number): string => {
    const fixed = Number(rate.toFixed(2));
    return Number.isInteger(fixed) ? fixed.toString() : fixed.toString();
  };

  const calculateTaxBreakdown = (
    items: any[],
    invoiceData?: InvoiceDataEntity | null,
  ): {
    rows: { label: string; amount: number }[];
    totalTax: number;
  } => {
    const taxGroups: Record<
      string,
      { type: "IGST" | "GST"; rate: number; amount: number }
    > = {};

    // Determine if it's inter-state or intra-state based on place_of_supply and destination_of_supply
    // Inter-state: place_of_supply !== destination_of_supply → IGST
    // Intra-state: place_of_supply === destination_of_supply → CGST+SGST
    const normalizeState = (state: string | null | undefined): string => {
      if (!state) return "";
      // Convert to uppercase and get abbreviation if needed
      const normalized = state.toUpperCase().trim();
      // If it's already a 2-letter code, return it
      if (normalized.length === 2) return normalized;
      // Otherwise, try to get abbreviation (this handles full state names)
      try {
        return getStateAbbreviation(state) || normalized;
      } catch {
        return normalized;
      }
    };

    const placeOfSupply = normalizeState(invoiceData?.place_of_supply);
    const destinationOfSupply = normalizeState(
      invoiceData?.destination_of_supply,
    );
    const hasSupplyInfo = placeOfSupply && destinationOfSupply;
    const isInterState = hasSupplyInfo && placeOfSupply !== destinationOfSupply;

    items.forEach((item) => {
      const taxName = (item.tax_name || "").toString();
      const taxPercentageRaw = item.tax_percentage;
      const taxRateFromPercentage =
        taxPercentageRaw !== undefined &&
        taxPercentageRaw !== null &&
        !isNaN(Number(taxPercentageRaw))
          ? parseFloat(taxPercentageRaw)
          : null;
      const parsedRateFromName = parseTaxRateFromName(taxName);
      const taxRate =
        taxRateFromPercentage !== null
          ? taxRateFromPercentage
          : parsedRateFromName !== null && !isNaN(parsedRateFromName)
            ? parsedRateFromName
            : 0;

      if (!taxRate) return;

      // Determine tax type:
      // 1. First check tax_name as it reflects what tax was actually applied
      // 2. If tax_name doesn't indicate IGST, then check place_of_supply and destination_of_supply
      // 3. Fallback to GST if neither provides clear indication
      let taxType: "IGST" | "GST";
      const taxNameUpper = taxName.toUpperCase();

      if (taxNameUpper.includes("IGST")) {
        // Tax name explicitly indicates IGST, use it
        taxType = "IGST";
      } else if (taxNameUpper.includes("GST")) {
        taxType = "GST";
      } else if (hasSupplyInfo) {
        // We have supply information, use it to determine tax type
        taxType = isInterState ? "IGST" : "GST";
      } else {
        // Fallback: if no clear indication, default to GST
        taxType = "GST";
      }

      const taxableAmount = (item.rate || 0) * (item.quantity || 0);
      const taxAmount = (taxableAmount * taxRate) / 100;

      const key = `${taxType}-${taxRate}`;
      if (!taxGroups[key]) {
        taxGroups[key] = { type: taxType, rate: taxRate, amount: 0 };
      }
      taxGroups[key].amount += taxAmount;
    });

    const rows: { label: string; amount: number }[] = [];

    Object.values(taxGroups).forEach((group) => {
      if (group.type === "IGST") {
        rows.push({
          label: `IGST ${formatRate(group.rate)}%`,
          amount: group.amount,
        });
      } else {
        const halfRate = group.rate / 2;
        const halfAmount = group.amount / 2;
        rows.push(
          {
            label: `SGST ${formatRate(halfRate)}%`,
            amount: halfAmount,
          },
          {
            label: `CGST ${formatRate(halfRate)}%`,
            amount: halfAmount,
          },
        );
      }
    });

    const totalTax = Object.values(taxGroups).reduce(
      (sum, group) => sum + group.amount,
      0,
    );

    return { rows, totalTax };
  };

  // Helper function to format date as "March 15, 2024"
  const formatDate = (
    dateString: string | null | undefined,
    fallback: string = "Not provided",
  ): string => {
    if (!dateString || dateString === "Not provided") {
      return fallback;
    }

    try {
      // ZOUR-19 (review 15): normalise first. A pipeline document's JSON date
      // is the extractor's DD/MM/YYYY, and `new Date("15/05/2026")` is Invalid
      // Date — so the Dates card read "Not provided" on a document the system
      // had already judged pre- or post-registration, which is the worst
      // possible thing for it to say next to the GST notice.
      const date = new Date(gstDay(dateString) ?? dateString);
      if (isNaN(date.getTime())) {
        return fallback;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return fallback;
    }
  };

  // Clean Architecture: Use queries hook from useInvoiceReview
  const {
    queries: queriesEntities,
    loading: queriesLoading,
    loadingMore: queriesLoadingMore,
    refetch: refetchQueries,
    hasMore: queriesHasMore,
    loadMore: loadMoreQueries,
  } = useInvoiceQueries(documentId);

  const [isQueryDetailModalOpen, setIsQueryDetailModalOpen] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>();

  // Check if there are any unresolved queries
  const hasUnresolvedQueries = queriesEntities.some(
    (q: any) => q.status !== "resolved",
  );
  const unresolvedCount = queriesEntities.filter(
    (q: any) => q.status !== "resolved",
  ).length;

  // Check if document is rejected or duplicate (read-only mode)
  const isDocumentReadOnly =
    invoiceDocument?.status === "3" || invoiceDocument?.status === "4";

  // Reset AI detection loading when overview is not rendered (e.g. customer not matched)
  const isAiOverviewRendered =
    !isDocumentReadOnly &&
    !isReviewPending &&
    !!invoiceJson &&
    !!invoiceDocument &&
    customerMatched;
  useEffect(() => {
    if (!isAiOverviewRendered) {
      setAiDetectionLoading(false);
    }
  }, [isAiOverviewRendered]);

  const documentStatusLabel =
    invoiceDocument?.status === "3"
      ? "Duplicate"
      : invoiceDocument?.status === "4"
        ? "Rejected"
        : null;

  // Function to save and navigate back (defined after state to avoid TypeScript errors)
  const saveAndGoBack = useCallback(async () => {
    if (!invoiceJson || !documentId) {
      console.error("No invoiceJson data or documentId to save");
      return;
    }

    // Check if GL override needs review (for non-posted docs)
    if (
      !isPostedToZoho &&
      pendingGLOverrideReview.length > 0 &&
      needsReviewForOverrideGLPermanent
    ) {
      // Collect all GL overrides that need review
      const glOverridesNeedingReview = pendingGLOverrideReview.filter(
        (gl) => gl.changeScope === "always",
      );

      if (glOverridesNeedingReview.length > 0) {
        // Build field_changes for all GL overrides
        const glFieldChanges = glOverridesNeedingReview.map((gl) => ({
          field: `Line Item ${gl.lineItemIndex + 1}: Account Name`,
          old: getAccountNameLeaf(gl.glData.old_account_name),
          new: getAccountNameLeaf(gl.glData.new_account_name),
        }));

        // Store all GL override data in array format
        const lineItemGLDataArray = glOverridesNeedingReview.map((gl) => ({
          line_item_index: gl.lineItemIndex,
          old_account_id: gl.glData.old_account_id,
          old_account_name: gl.glData.old_account_name,
          new_account_id: gl.glData.new_account_id,
          new_account_name: gl.glData.new_account_name,
          change_scope: gl.changeScope,
          reason: gl.reason,
          override_data: gl.overrideData,
        }));

        // Show review modal for GL override
        const glChanges: ChangeDetails = {
          reason: glOverridesNeedingReview[0].reason, // Use first reason as primary
          old_value: glOverridesNeedingReview[0].glData.old_account_name,
          new_value: glOverridesNeedingReview[0].glData.new_account_name,
          field_changes: glFieldChanges,
          line_item_index: glOverridesNeedingReview[0].lineItemIndex, // Keep for backward compatibility
          line_item_gl_data: lineItemGLDataArray as any, // Store as array
          change_scope: glOverridesNeedingReview[0].changeScope,
          raw_json: invoiceJsonRef.current || invoiceJson,
          original_json: originalInvoiceJson ?? undefined,
          validation_snapshot: buildValidationSnapshotForReview(
            validationBaselineLineItemsRef.current,
            invoiceJsonRef.current || invoiceJson,
          ),
          action_types: ["override_gl_permanent"],
          // Include the override data for semantic override API call (use first one for backward compatibility)
          override_data: glOverridesNeedingReview[0].overrideData,
        };

        setPendingReviewAction({
          action: async () => {
            // After review is submitted, navigate back
            handleGoBack();
          },
          changeDetails: glChanges,
          disableSubmit: false,
        });
        setIsReviewModalOpen(true);
        return;
      }
    }

    // Check if update needs review (for non-posted docs)
    // Note: update should never require review, but checking for safety
    // This is mainly for future-proofing

    try {
      // Clear any pending debounced save
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Save immediately
      await debouncedSave(invoiceJson);

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectCustomer");

      // Navigate back to invoices section
      handleGoBack();
    } catch (error) {
      console.error("Failed to save before navigation:", error);
      // Still navigate back even if save fails
      handleGoBack();
    }
  }, [
    invoiceJson,
    documentId,
    debouncedSave,
    handleGoBack,
    isPostedToZoho,
    pendingGLOverrideReview,
    needsReviewForOverrideGLPermanent,
  ]);

  // Line item editing state
  const [isEditLineItemModalOpen, setIsEditLineItemModalOpen] = useState(false);
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<
    number | null
  >(null);
  const [editingLineItemData, setEditingLineItemData] =
    useState<LineItemEntity | null>(null);
  const [originalAccountName, setOriginalAccountName] = useState<string>("");
  const [originalAccountId, setOriginalAccountId] = useState<string>("");
  const [lineItems, setLineItems] = useState(invoiceJson?.line_items || []);

  // === Inline Credit A/c GL picker (invoices) — mirror of the bills Debit A/c
  // picker. For LLM lines the Credit A/c starts EMPTY and the reviewer must pick
  // from the ranked AI list. UI-only: account_id/account_name are never cleared,
  // so save/capture/posting are unaffected. A line is "confirmed" once picked
  // inline OR saved via the edit modal. ===
  const [glConfirmedLines, setGlConfirmedLines] = useState<
    Record<number, boolean>
  >({});
  const [openInlineGlIndex, setOpenInlineGlIndex] = useState<number | null>(
    null,
  );
  // Re-arm the inline GL picker whenever a different invoice is opened.
  useEffect(() => {
    setGlConfirmedLines({});
    setOpenInlineGlIndex(null);
  }, [documentId]);

  const inlineGlSuggestionsForLine = useCallback(
    (idx: number): InlineGlSuggestion[] => {
      const map =
        (invoiceDoc?.gl_line_suggestions as Record<string, any[]>) || {};
      const list = map[String(idx)];
      return Array.isArray(list) ? (list as InlineGlSuggestion[]) : [];
    },
    [invoiceDoc],
  );

  // Only show the empty picker when GL fields are actually editable (normal
  // review — not read-only/posted/audit).
  const inlineGlPickerActive =
    !shouldDisableEditableFields && !isPostedToZoho && !isDocumentReadOnly;

  // A line shows the empty picker when it's an LLM line the reviewer hasn't
  // picked yet and there are ranked candidates (gl_line_suggestions[0].source_layer === "llm").
  const lineNeedsGlPick = useCallback(
    (idx: number): boolean => {
      const sugg = inlineGlSuggestionsForLine(idx);
      return (
        sugg.length > 0 &&
        sugg[0]?.source_layer === "llm" &&
        !glConfirmedLines[idx]
      );
    },
    [glConfirmedLines, inlineGlSuggestionsForLine],
  );

  // For non-LLM lines still carrying the AI's top pick, expose the source layer
  // + reason for a compact "Source: <label>" caption with an info tooltip.
  const inlineGlBadgeMeta = useCallback(
    (
      idx: number,
      lineItem: any,
    ): { label: string; reason: string; confidence: number | null } | null => {
      const top = inlineGlSuggestionsForLine(idx)[0];
      if (!top || top.source_layer === "llm") return null;
      if ((lineItem?.account_id || "") !== (top.account_id || "")) return null;
      const labelMap: Record<string, string> = {
        knn: "kNN",
        override: "Override",
        clf: "Model",
      };
      return {
        label: labelMap[top.source_layer || ""] || top.source_layer || "AI",
        reason: top.reason || "",
        confidence: top.confidence ?? null,
      };
    },
    [inlineGlSuggestionsForLine],
  );

  const handleInlineGlSelect = (
    index: number,
    suggestion: InlineGlSuggestion,
  ) => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be edited while validation is running.");
      return;
    }
    const updatedLineItems = [...lineItems];
    const prev = updatedLineItems[index];
    if (!prev) return;
    const accountChanged =
      (prev.account_id || "") !== (suggestion.account_id || "");
    updatedLineItems[index] = {
      ...prev,
      account_id: suggestion.account_id,
      account_name: suggestion.gl_name,
      gl_confidence: accountChanged ? 0 : prev.gl_confidence,
    };
    setLineItems(updatedLineItems);
    updateLineItems(updatedLineItems);
    setGlConfirmedLines((p) => ({ ...p, [index]: true }));
    setOpenInlineGlIndex(null);
  };
  // Memoize tax breakdown calculation to prevent infinite loops

  const taxBreakdown = useMemo(() => {
    return calculateTaxBreakdown(lineItems, invoiceJson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, invoiceJson]);

  // Update GL Dialog state
  const [isUpdateGLDialogOpen, setIsUpdateGLDialogOpen] = useState(false);
  const [pendingGLUpdate, setPendingGLUpdate] = useState<{
    changedLineItemIndex: number;
    oldAccountName: string;
    newAccountName: string;
    newAccountId: string;
  } | null>(null);

  // Initialize hooks
  const {
    taxes,
    loading: taxesLoading,
    fetchTaxSpecification: loadTaxSpecification,
  } = useTaxes();

  const { determineGSTType } = useGSTDetermination();

  // Validation state
  const [validationData, setValidationData] = useState<any>(null);
  const [isValidationProgressModalOpen, setIsValidationProgressModalOpen] =
    useState(false);
  const [validationPassed, setValidationPassed] = useState(false);
  const [failedValidationMessages, setFailedValidationMessages] = useState<
    string[]
  >([]);
  // True once the user edits the invoice-number field on this screen. The AI
  // Detection panel's Document Number Check reads the SAVED number (DB), so its
  // failure goes stale after an edit — the panel hides it and shows a note.
  const [invoiceNumberEdited, setInvoiceNumberEdited] = useState(false);

  // GL Classification Modal state
  const [isGLClassificationModalOpen, setIsGLClassificationModalOpen] =
    useState(false);
  const [glClassificationLineItems, setGLClassificationLineItems] = useState<
    GLClassificationLineItem[]
  >([]);

  // "Apply this GL automatically next time?" modal state.
  // Shown on Approve when one or more line items still carry the AI-suggested
  // GL (i.e. the user has not edited the account name). Picking
  // "Save as default suggestion" calls createSemanticOverride directly for the
  // listed items without going through any review flow.
  const [isApplyGLAsDefaultOpen, setIsApplyGLAsDefaultOpen] = useState(false);
  const [applyGLAsDefaultItems, setApplyGLAsDefaultItems] = useState<
    ApplyGLAsDefaultLineItem[]
  >([]);
  const [isApplyingGLDefaults, setIsApplyingGLDefaults] = useState(false);
  // Per-line-item gl_source_layer from the validation API. Populated in
  // handleValidationComplete so buildApplyGLAsDefaultItems can gate on "llm".
  const [glSourceLayerMap, setGlSourceLayerMap] = useState<
    Record<number, string>
  >({});

  // Tax state
  // Approval loading state
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);

  // GST enabled state
  const [isGstEnabled, setIsGstEnabled] = useState(true);

  // Discount state (not from API, user-editable)
  const [discount, setDiscount] = useState<number>(0);

  // Handler for discount changes - updates both state and invoiceJson
  const handleDiscountChange = useCallback(
    (newDiscount: number) => {
      setDiscount(newDiscount);
      updateInvoiceJson({ discount: newDiscount });
    },
    [updateInvoiceJson],
  );

  // Update lineItems when invoiceJson changes
  useEffect(() => {
    if (invoiceJson?.line_items) {
      // Only update if lineItems doesn't already match invoiceJson.line_items
      // This prevents unnecessary re-renders and race conditions
      setLineItems((prevLineItems) => {
        // Quick reference check first
        if (prevLineItems === invoiceJson.line_items) {
          return prevLineItems; // Same reference, no update needed
        }

        // Deep comparison of tax-related fields to detect actual changes
        const hasChanges =
          prevLineItems.length !== invoiceJson.line_items.length ||
          prevLineItems.some((item, index) => {
            const newItem = invoiceJson.line_items[index];
            if (!newItem) return true;
            // Check for tax-related changes (primary concern) and other critical fields
            return (
              item.tax_id !== newItem.tax_id ||
              item.tax_name !== newItem.tax_name ||
              item.tax_percentage !== newItem.tax_percentage ||
              item.rate !== newItem.rate ||
              item.quantity !== newItem.quantity
            );
          });

        // Only update if there are actual changes
        if (hasChanges) {
          // Return new array reference to trigger memo recalculation
          return [...invoiceJson.line_items];
        }

        return prevLineItems;
      });
    }
  }, [invoiceJson?.line_items, invoiceJson?.gst_treatment]);

  // ZOUR-19: does GST apply to THIS invoice?
  //
  // Was the company-wide flag alone. A invoice dated before
  // `gstEnabledFrom` now resolves to false as well, so tax columns, tax rates,
  // the tax breakdown and the ITC picker all disappear together — every one of
  // them already hangs off `isGstEnabled`, which is why this is the only line
  // that had to change.
  //
  // Re-runs when the date changes, not just on mount: an edit that moves the
  // invoice across the boundary has to re-draw the form.
  //
  // ZOUR-92: and on the REGISTRATION it is issued from — see the bill twin.
  useEffect(() => {
    setIsGstEnabled(
      gstAppliesToDocumentFor(
        invoiceJson?.date || fetchedInvoiceJson?.date || "",
        invoiceJson?.location_id ?? fetchedInvoiceJson?.location_id ?? null,
      ),
    );
  }, [
    invoiceJson?.date,
    fetchedInvoiceJson?.date,
    invoiceJson?.location_id,
    fetchedInvoiceJson?.location_id,
    activeLocations,
  ]);

  // ZOUR-19: the same question, as a value the JSX can read, so the page can
  // SAY why the tax fields are gone. The notice is derived from the date in
  // front of the user rather than from validation results, so it is right
  // immediately and right on a document that has never been validated.
  const showPreGstNotice = isPreGstRegistrationFor(
    invoiceJson?.date || fetchedInvoiceJson?.date || "",
    invoiceJson?.location_id ?? fetchedInvoiceJson?.location_id ?? null,
  );

  // Initialize discount from invoiceJson if it exists
  useEffect(() => {
    if (invoiceJson?.discount !== undefined) {
      setDiscount(invoiceJson.discount);
    }
  }, [invoiceJson?.discount]);

  // Calculate GST value from line items
  const calculatedGstValue = lineItems.reduce((sum, item) => {
    const taxPercentage = item.tax_percentage || 0;
    const taxAmount =
      ((item.rate || 0) * (item.quantity || 0) * taxPercentage) / 100;
    return sum + taxAmount;
  }, 0);

  /**
   * Pending-review strike maps: from persisted `review.change_details` only
   * (`field_changes` + optional original/raw snapshot for line totals), not live
   * `invoiceJson` — avoids recomputing on every edit while the doc is in review.
   */
  const invoiceFbacDiffMaps = useMemo(() => {
    const emptyHeader = new Map<string, { oldText: string; newText: string }>();
    const emptyLine = new Map<string, { oldText: string; newText: string }>();
    if (!isReviewPending) {
      return { header: emptyHeader, line: emptyLine };
    }
    return buildInvoicePendingReviewDisplayMapsFromChangeDetails(
      review?.change_details,
      taxes,
    );
  }, [isReviewPending, review?.change_details, taxes]);

  const fbacHd = (key: string) => invoiceFbacDiffMaps.header.get(key);

  const fbacLineDiff = (lineOneBased: number, fieldKey: string) =>
    invoiceFbacDiffMaps.line.get(`${lineOneBased}-${fieldKey}`);

  const companyCurrencyCode = useMemo(() => {
    const c = getCompany();
    const code = c?.currency;
    return String(code ?? "")
      .trim()
      .toUpperCase();
  }, []);

  const documentCurrencyCode = String(invoiceJson?.currency_code ?? "")
    .trim()
    .toUpperCase();

  const showExchangeRateField =
    !!companyCurrencyCode &&
    !!documentCurrencyCode &&
    companyCurrencyCode !== documentCurrencyCode;

  const accountingBackend = getStoredAccountingBackend();
  const isTallyBackend = accountingBackend === "tally";
  const exchangeRateRequired = isTallyBackend && showExchangeRateField;
  const exchangeRatePostContext = useMemo(
    () => ({
      userSet: !!invoiceJson?.exchange_rate_user_set,
      isPosted: isPostedToZoho,
    }),
    [invoiceJson?.exchange_rate_user_set, isPostedToZoho],
  );

  const { loading: exchangeRateLoading } = useZohoDocumentExchangeRate({
    enabled: !isTallyBackend && showExchangeRateField,
    currencyId: invoiceJson?.currency_id,
    fromDate: invoiceJson?.date || invoiceJson?.invoice_date,
    currentRate: invoiceJson?.exchange_rate,
    onRateFetched: (rate) => {
      setInvoiceJson((prev) => {
        if (!prev) return prev;
        if (!needsZohoExchangeRateFetch(prev.exchange_rate)) return prev;
        return {
          ...prev,
          exchange_rate: rate,
          exchange_rate_user_set: false,
        };
      });
    },
  });

  //  All these info from the json or options json
  const basicInfo = useMemo(
    () => ({
      invoiceType: "INVOICES",
      invoiceNumber: getFieldValue(invoiceJson?.invoice_number, "Not provided"),
      currency: getFieldValue(invoiceJson?.currency_code, "USD"),
      exchange_rate: formatExchangeRateForBasicInfoField(
        invoiceJson?.exchange_rate,
        {
          showFx: showExchangeRateField,
          backend: accountingBackend,
          userSet: invoiceJson?.exchange_rate_user_set,
        },
      ),
      exchange_rate_user_set: invoiceJson?.exchange_rate_user_set,
      optionsData: {
        invoiceNumber: [],
        currency: [],
      },
    }),
    [
      invoiceJson?.invoice_number,
      invoiceJson?.currency_code,
      invoiceJson?.exchange_rate,
      invoiceJson?.exchange_rate_user_set,
      showExchangeRateField,
      accountingBackend,
    ],
  );

  const datesInfo = {
    documentDate: formatDate(invoiceJson?.date, "Not provided"),
    postingDate: formatDate(invoiceJson?.date, "Not provided"),
    dueDate: formatDate(invoiceJson?.due_date, "Not provided"),
    optionsData: {
      documentDate: [],
      postingDate: [],
      dueDate: [],
    },
  };

  const taxInfo = {
    vendorGstin: getFieldValue(
      invoiceJson?.vendor_gstin || invoiceJson?.gst_no,
      "Not provided",
    ),
    customerGstin: getFieldValue(invoiceJson?.customer_gstin, "Not provided"),
    // gstRate: invoiceJson?.tax_total, // This would need to be calculated from line items
    taxBreakdown: `Total Tax: ${getFieldValue(
      invoiceJson?.currency_symbol,
      "₹",
    )}${formatAmount(calculatedGstValue, "0.00")}`,
    optionsData: {
      vendorGstin: [],
      customerGstin: [],
      gstRate: [],
      taxBreakdown: [],
    },
    isGstEnabled: isGstEnabled,
  };

  const documentMetadata = {
    source: "INVOICES",
    uploadedBy: "System", // This would come from the invoice record
    uploadTimestamp: "Not provided", // This would come from the invoice record
    fileName: fileName || "document.pdf",
    optionsData: {
      uploadedBy: [],
      uploadTimestamp: [],
      fileName: [],
    },
  };

  const amountSummary = {
    subtotal: `${getFieldValue(
      invoiceJson?.currency_symbol,
      "₹",
    )}${formatAmount(invoiceJson?.sub_total, "0.00")}`,
    gstValue: `${getFieldValue(
      invoiceJson?.currency_symbol,
      "₹",
    )}${formatAmount(calculatedGstValue, "0.00")}`,
    discountsAdjustments:
      discount !== 0
        ? `${invoiceJson?.currency_symbol || "₹"}${formatAmount(
            discount,
            "0.00",
          )}`
        : "No adjustments",
    totalAmount: `${getFieldValue(
      invoiceJson?.currency_symbol,
      "₹",
    )}${formatAmount(invoiceJson?.total, "0.00")}`,
    optionsData: {
      subtotal: [],
      gstValue: [],
      discountsAdjustments: [],
      totalAmount: [],
    },
    isGstEnabled: isGstEnabled,
  };

  // Consolidated initialization and cleanup useEffect
  useEffect(() => {
    // Initialize lastSavedDataRef with initial data
    if (initialInvoiceJson && !lastSavedDataRef.current) {
      lastSavedDataRef.current = JSON.stringify(initialInvoiceJson);
    }

    // Copy ref value for cleanup
    const currentTimeout = debounceTimeoutRef.current;

    // Cleanup function
    return () => {
      // document.removeEventListener("keydown", handleKeyDown);
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, [initialInvoiceJson, invoiceJson]);

  // Handle document URL refresh from DocumentViewer
  // Note: signedUrl and fileName are now managed by the useDocumentReviewData hook
  // and automatically updated when the invoice is refetched
  const handlePdfUrlRefresh = useCallback(
    (newUrl: string, newFileName: string) => {
      console.log("PDF URL refreshed:", newUrl, newFileName);
      // These are now read-only from the hook, so we just log
      // The hook will handle updates when data is refetched
    },
    [],
  );

  // Memoize DocumentViewer props to prevent unnecessary re-renders
  const documentViewerProps = useMemo(
    () => ({
      documentId: documentId ? parseInt(documentId) : undefined,
      documentType: "invoice" as const,
      pdfUrl: signedUrl || undefined,
      fileName: fileName || "document.pdf",
      className: "w-1/2",
      onError: (error: any) => console.error("PDF Error:", error),
      onUrlRefresh: handlePdfUrlRefresh,
    }),
    [documentId, signedUrl, fileName, handlePdfUrlRefresh],
  );

  // Note: Queries are now automatically fetched via useQueriesBySource hook

  // Validate invoice before approval/update
  const validateInvoice = (): boolean => {
    // Clear all previous validation errors first
    clearErrors();

    const newErrors: Record<string, string> = {};

    // Check if customer is matched
    if (!customerMatched) {
      newErrors.customer =
        "Customer verification required. Please search and match a customer.";
    }

    // Check if currency is INR
    // if (invoiceJson?.currency_code && invoiceJson.currency_code !== "INR") {
    //   newErrors.currency = `This feature is not built for currencies other than INR yet. Current currency: ${invoiceJson.currency_code}`;
    // }

    // Check line item tax validation
    const lineItemTaxValidation = validateLineItemsTaxInfo();
    if (!lineItemTaxValidation.isValid) {
      newErrors.lineItemTax =
        lineItemTaxValidation.errorMessage ||
        "Line items are missing tax information";
    }

    if (
      exchangeRateRequired &&
      isExchangeRateMissingForTallyPost(invoiceJson?.exchange_rate, {
        userSet: invoiceJson?.exchange_rate_user_set,
        isPosted: isPostedToZoho,
      })
    ) {
      newErrors.exchange_rate =
        "Exchange rate is required for foreign currency documents when posting to Tally.";
    }

    // Set all validation errors at once (after clearing)
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  // ── Customer-change pipeline rerun ──────────────────────────────────────
  // When the accountant manually changes the customer, re-run the post-match
  // pipeline (GL + supply/tax) on the backend, poll until done, then reload so
  // the recomputed values show. Mirror of the bills vendor-change rerun, minus
  // TDS (TDS is buyer-side and does not apply to sales invoices).
  const [isRecomputing, setIsRecomputing] = useState(false);
  const recomputePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Customer pending re-run confirmation (drives CustomerRerunConfirmModal).
  // Null when no confirm is open.
  const [pendingCustomerRerun, setPendingCustomerRerun] = useState<{
    customerId: string;
    customerName: string;
    oldCustomerName?: string | null;
  } | null>(null);
  // Error surfaced inside CustomerRerunConfirmModal if the re-run fails.
  const [rerunError, setRerunError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (recomputePollRef.current) {
        clearInterval(recomputePollRef.current);
        recomputePollRef.current = null;
      }
    };
  }, []);

  const triggerCustomerRerun = useCallback(
    async (customerId: string, customerName: string) => {
      if (!documentId || !customerId) return;
      setRerunError(null);
      try {
        setIsRecomputing(true);
        await rerunCustomerChange(documentId, customerId, customerName);

        if (recomputePollRef.current) clearInterval(recomputePollRef.current);
        let elapsed = 0;
        recomputePollRef.current = setInterval(async () => {
          elapsed += 2000;
          try {
            const res = await getRecomputeStatus(documentId);
            const finished =
              res.status === "done" ||
              res.status === "error" ||
              res.status === "idle";
            if (finished || elapsed > 180000) {
              if (recomputePollRef.current) {
                clearInterval(recomputePollRef.current);
                recomputePollRef.current = null;
              }
              setIsRecomputing(false);
              if (res.status === "done") {
                // Reload so recomputed GL / tax / totals are shown. Clear router
                // history state first: location.state holds the pre-rerun document
                // and would otherwise re-hydrate stale. documentId still recovers
                // from sessionStorage, so we stay on this invoice and the hook
                // re-fetches the fresh invoice_json.
                window.history.replaceState(null, "");
                window.location.reload();
              } else if (res.status === "error") {
                setRerunError(
                  res.message || "Re-run failed. Please try again.",
                );
              } else if (elapsed > 180000) {
                setRerunError("Re-run timed out. Please try again.");
              } else {
                setRerunError("Re-run did not complete. Please try again.");
              }
            }
          } catch {
            if (recomputePollRef.current) {
              clearInterval(recomputePollRef.current);
              recomputePollRef.current = null;
            }
            setIsRecomputing(false);
            setRerunError(
              "Lost connection while re-running. Please try again.",
            );
          }
        }, 2000);
      } catch (err) {
        setIsRecomputing(false);
        setRerunError("Could not start the re-run. Please try again.");
        console.error("Customer rerun failed to start:", err);
      }
    },
    [documentId],
  );

  // Add handler for customer selection
  const handleCustomerSelect = useCallback(
    (customer: any, isManualSelection: boolean = false): boolean => {
      // Use ref to get current invoiceJson to avoid dependency issues
      const currentInvoiceJson = invoiceJsonRef.current;
      // Snapshot the customer already on the invoice BEFORE updateInvoiceJson
      // runs, so the re-run decision below can tell whether the pick actually
      // changed the customer. Primitives, so a later in-place update can't mutate
      // them.
      const previousCustomerId = String(
        currentInvoiceJson?.customer_id ?? "",
      ).trim();
      const previousCustomerName = currentInvoiceJson?.customer_name ?? null;

      clearFieldError("customerCurrency");

      const customerCurrencyId = String(customer.currency_id ?? "").trim();
      const invoiceCurrencyId = String(
        currentInvoiceJson?.currency_id ?? "",
      ).trim();

      // Check if customer currency matches invoice currency (silent check, will validate on submit)
      if (
        customerCurrencyId &&
        invoiceCurrencyId &&
        customerCurrencyId !== invoiceCurrencyId
      ) {
        setFieldError(
          "customerCurrency",
          `Currency mismatch: Please select a customer with matching currency.`,
        );
        return false; // Don't proceed with selection
      }

      // Get customer data with fallbacks for different field name variations
      const customerName = customer.customer_name || customer.name;
      const customerAddress = customer.billing_address || customer.address;
      const customerGstin = customer.gstin || customer.gstin_vat_tax_id;

      // Use zoho_customerid if available, otherwise fall back to customer_id
      const customerId = customer.zoho_customerid || customer.customer_id;

      // Update customer data if customer has required information
      // Accept either zoho_customerid or customer_id (for newly created customers)
      if (customerId && (customerName || customerAddress)) {
        setCustomerMatched(true);
        clearFieldError("customer"); // Clear customer validation error
        const customerSupplyLocation = getSupplyLocationCode(
          customer.state,
          customer.country,
        );

        const isOverseas = customer.gst_treatment === "overseas";
        if (isOverseas) {
          loadTaxSpecification("inter");
        }

        // Build update object
        const updateData: any = {
          customer_id: customerId, // Use the resolved customerId (zoho_customerid or customer_id)
          customer_address: customerAddress,
          customer_name: customerName,
          customer_gstin: customerGstin,
          gst_no: customerGstin,
          gst_treatment: customer.gst_treatment,
          payment_terms: customer.payment_terms,
          place_of_supply: customerSupplyLocation,
          destination_of_supply: customerSupplyLocation,
        };

        updateInvoiceJson(updateData);

        // If this is a manual selection, mark it in the ref to prevent auto-select from overriding
        if (isManualSelection) {
          const manualSelectionHash = JSON.stringify({
            customer_id: customerId,
            customer_name: customerName,
            customer_gstin: customerGstin,
            customer_address: customerAddress,
            gst_treatment: customer.gst_treatment,
            payment_terms: customer.payment_terms,
            place_of_supply: customerSupplyLocation,
            destination_of_supply: customerSupplyLocation,
          });

          processedCustomerUpdateRef.current = {
            customerId,
            timestamp: Date.now(), // Current time for manual selection
            customerDataHash: manualSelectionHash,
            isManualSelection: true, // Mark as manual selection
            // Don't set sessionStorageTimestamp for manual selections
          };

          // Re-run the pipeline (GL + supply/tax) for the newly chosen customer —
          // but only when the customer actually changed. Re-picking the same
          // customer already on the invoice would needlessly wipe and recompute
          // classifications (including manual GL edits) for no real change.
          if (
            previousCustomerId &&
            String(customerId).trim() === previousCustomerId
          ) {
            return true; // same customer — nothing to recompute
          }
          // Gate the re-run behind an explicit confirm modal, since it overwrites
          // the current classifications including any manual edits.
          setPendingCustomerRerun({
            customerId,
            customerName,
            // First-time assignment is keyed off a real matched customer_id, not
            // the OCR-detected customer_name (unmatched invoices carry an extracted
            // name with no id). No prior match → no "overwrite" warning, "classify"
            // wording.
            oldCustomerName: previousCustomerId ? previousCustomerName : null,
          });
        }

        return true;
      }

      return false;
    },
    [updateInvoiceJson, setFieldError, clearFieldError, loadTaxSpecification],
  );

  // Ref to track processed customer updates to prevent infinite loops
  // Also tracks manual selections to prevent auto-select from overriding them
  const processedCustomerUpdateRef = useRef<{
    customerId: string;
    timestamp: number; // Timestamp of the ref update (not sessionStorage timestamp)
    customerDataHash: string;
    isManualSelection?: boolean; // Flag to indicate manual selection from modal
    sessionStorageTimestamp?: number; // Store sessionStorage timestamp if from auto-select
  } | null>(null);

  // Auto-select customer after creating/editing
  useEffect(() => {
    const autoSelectData = sessionStorage.getItem("autoSelectCustomer");

    if (autoSelectData) {
      try {
        const parsed = JSON.parse(autoSelectData);

        const { customer, timestamp: sessionStorageTimestamp, action } = parsed;

        // Use zoho_customerid if available, otherwise fall back to customer_id
        const storedCustomerId =
          customer.zoho_customerid || customer.customer_id;

        // Create a hash of customer data to detect actual changes
        const customerName = customer.customer_name || customer.name;
        const customerAddress = customer.billing_address || customer.address;
        const customerGstin = customer.gstin || customer.gstin_vat_tax_id;
        const customerDataHash = JSON.stringify({
          customer_id: storedCustomerId,
          customer_name: customerName,
          customer_gstin: customerGstin,
          customer_address: customerAddress,
          gst_treatment: customer.gst_treatment,
          payment_terms: customer.payment_terms,
          place_of_supply: getSupplyLocationCode(
            customer.state,
            customer.country,
          ),
          destination_of_supply: getSupplyLocationCode(
            customer.state,
            customer.country,
          ),
        });

        // CRITICAL: Skip auto-select if there's a manual selection for a DIFFERENT customer
        // Manual selections always take precedence unless it's the same customer with newer data
        const currentTime = Date.now();
        const hasManualSelection =
          processedCustomerUpdateRef.current?.isManualSelection;
        const manuallySelectedCustomerId =
          processedCustomerUpdateRef.current?.customerId;

        if (hasManualSelection && processedCustomerUpdateRef.current) {
          // Check if the sessionStorage customer is different from what was manually selected
          const isDifferentCustomer =
            manuallySelectedCustomerId &&
            storedCustomerId &&
            manuallySelectedCustomerId !== storedCustomerId;

          if (isDifferentCustomer) {
            // If it's a different customer, check how recent the manual selection was
            const manualSelectionAge =
              currentTime - processedCustomerUpdateRef.current.timestamp;

            // If manual selection was recent (< 10 seconds), block to prevent race conditions
            // If manual selection was old (> 10 seconds), allow auto-select (user is switching customers)
            if (manualSelectionAge < 10000) {
              return;
            } else {
              // Manual selection is old, user is switching customers - allow auto-select
              // Clear the manual selection flag since we're switching to a different customer

              // Clear the manual selection flag to allow this auto-select
              processedCustomerUpdateRef.current.isManualSelection = false;
              // Continue with auto-select below
            }
          } else {
            // Same customer - use existing logic
            // If it's the same customer, only allow auto-select if:
            // 1. SessionStorage timestamp is newer than manual selection (recent edit)
            // 2. Manual selection wasn't too recent (within 10 seconds)
            const sessionStorageIsNewer =
              !processedCustomerUpdateRef.current.sessionStorageTimestamp ||
              sessionStorageTimestamp >
                processedCustomerUpdateRef.current.sessionStorageTimestamp;

            const manualSelectionRecent =
              currentTime - processedCustomerUpdateRef.current.timestamp <
              10000;

            // Only proceed if sessionStorage is newer AND manual selection wasn't too recent
            if (!sessionStorageIsNewer || manualSelectionRecent) {
              return;
            }
          }
        }

        // Only process if the data is recent (within last 60 seconds)
        if (currentTime - sessionStorageTimestamp < 60000) {
          // Wait for invoice data to be loaded before processing (for both created and updated)
          // updateInvoiceJson requires invoiceJson or fetchedInvoiceJson to exist and isDataLoading to be false
          if (isDataLoading && !fetchedInvoiceJson) {
            return; // Don't clear sessionStorage, will retry when data loads
          }

          // For "created" actions, check if current invoiceJson already has this customer
          if (action === "created") {
            // Create hash of CURRENT invoice data for comparison
            const currentInvoiceCustomerHash = invoiceJson
              ? JSON.stringify({
                  customer_id: invoiceJson.customer_id,
                  customer_name: invoiceJson.customer_name,
                  customer_gstin: invoiceJson.customer_gstin,
                  customer_address: invoiceJson.customer_address,
                  gst_treatment: invoiceJson.gst_treatment,
                  payment_terms: invoiceJson.payment_terms,
                  place_of_supply: invoiceJson.place_of_supply,
                  destination_of_supply: invoiceJson.destination_of_supply,
                })
              : null;

            // Only skip if:
            // 1. We've already processed this exact action (same timestamp)
            // 2. AND the current invoiceJson already matches the new customer data (data already applied)
            const alreadyProcessedAndApplied =
              processedCustomerUpdateRef.current &&
              processedCustomerUpdateRef.current.customerId ===
                storedCustomerId &&
              processedCustomerUpdateRef.current.sessionStorageTimestamp ===
                sessionStorageTimestamp &&
              currentInvoiceCustomerHash === customerDataHash &&
              !processedCustomerUpdateRef.current.isManualSelection;

            if (alreadyProcessedAndApplied) {
              return;
            }

            // Mark as processed BEFORE calling handleCustomerSelect
            processedCustomerUpdateRef.current = {
              customerId: storedCustomerId,
              timestamp: currentTime,
              customerDataHash,
              isManualSelection: false, // Auto-select is not manual
              sessionStorageTimestamp: sessionStorageTimestamp, // Store sessionStorage timestamp
            };

            handleCustomerSelect(customer, false); // false = not manual
            return;
          }

          // For "updated" actions, check if already processed
          const currentCustomerId = invoiceJson?.customer_id;

          // Create hash of CURRENT invoice data for comparison
          const currentInvoiceCustomerHash = invoiceJson
            ? JSON.stringify({
                customer_id: invoiceJson.customer_id,
                customer_name: invoiceJson.customer_name,
                customer_gstin: invoiceJson.customer_gstin,
                customer_address: invoiceJson.customer_address,
                gst_treatment: invoiceJson.gst_treatment,
                payment_terms: invoiceJson.payment_terms,
                place_of_supply: invoiceJson.place_of_supply,
                destination_of_supply: invoiceJson.destination_of_supply,
              })
            : null;

          // Check if we've already processed this exact update
          // Only skip if:
          // 1. We have a processed ref
          // 2. The customer ID matches
          // 3. The sessionStorage timestamp matches (same edit session) - THIS IS KEY: different timestamp = new edit
          // 4. The CURRENT invoice data hash matches the new customer data hash (data already applied)
          // 5. AND it's not a manual selection
          const alreadyProcessed =
            processedCustomerUpdateRef.current &&
            processedCustomerUpdateRef.current.customerId ===
              storedCustomerId &&
            processedCustomerUpdateRef.current.sessionStorageTimestamp ===
              sessionStorageTimestamp && // Different timestamp = proceed
            currentInvoiceCustomerHash === customerDataHash &&
            !processedCustomerUpdateRef.current.isManualSelection;

          if (alreadyProcessed) {
            // Already processed this exact update and data is already applied - skip to prevent loop
            return;
          }

          // Check if customer data actually changed (compare new data with current invoice data)
          const customerDataChanged =
            !invoiceJson?.customer_id ||
            invoiceJson.customer_id !== storedCustomerId ||
            invoiceJson?.customer_name !== customerName ||
            invoiceJson?.customer_gstin !== customerGstin ||
            invoiceJson?.customer_address !== customerAddress ||
            invoiceJson?.gst_treatment !== customer.gst_treatment ||
            invoiceJson?.payment_terms !== customer.payment_terms ||
            invoiceJson?.place_of_supply !==
              getSupplyLocationCode(customer.state, customer.country) ||
            invoiceJson?.destination_of_supply !==
              getSupplyLocationCode(customer.state, customer.country);

          // Update if:
          // 1. Customer IDs DON'T match (different customer) - always update
          // 2. OR there's no current customer set - always update
          // 3. OR customer matches AND data actually changed
          const shouldUpdate =
            !currentCustomerId ||
            currentCustomerId !== storedCustomerId ||
            (currentCustomerId === storedCustomerId && customerDataChanged);

          if (shouldUpdate) {
            // Mark as processed BEFORE calling handleCustomerSelect
            processedCustomerUpdateRef.current = {
              customerId: storedCustomerId,
              timestamp: currentTime,
              customerDataHash,
              isManualSelection: false, // Auto-select is not manual
              sessionStorageTimestamp: sessionStorageTimestamp, // Store sessionStorage timestamp
            };

            handleCustomerSelect(customer, false); // false = not manual
          } else {
            // Customer matches and no data changed - mark as processed to prevent future triggers
            processedCustomerUpdateRef.current = {
              customerId: storedCustomerId,
              timestamp: currentTime,
              customerDataHash,
              isManualSelection: false,
              sessionStorageTimestamp: sessionStorageTimestamp,
            };
          }
        } else {
          // Data is stale, clear ref (but keep sessionStorage for reference)
          // Don't clear sessionStorage here - it might be needed for future loads
          processedCustomerUpdateRef.current = null;
        }
      } catch (error) {
        processedCustomerUpdateRef.current = null;
      }
    }
  }, [
    handleCustomerSelect,
    invoiceJson,
    fetchedInvoiceJson,
    isDataLoading,
    isPostedToZoho,
  ]);

  // Effect for initial data fetching (GL mappings and taxes) - runs once on mount
  // Load GST determination and taxes AFTER permissions and document are loaded
  useEffect(() => {
    // Wait for main data (permissions + document) to finish loading
    if (isDataLoading) return;

    // Wait for invoice data to be available
    if (!initialInvoiceJson && !fetchedInvoiceJson) return;

    let mounted = true;

    const loadInitialData = async () => {
      try {
        // Use the initial data or fetched data for GST determination
        const invoiceDataForGST = initialInvoiceJson || fetchedInvoiceJson;
        if (!invoiceDataForGST) return;

        if (invoiceDataForGST.gst_treatment?.toLowerCase() === "overseas") {
          if (mounted) await loadTaxSpecification("inter");
          return;
        }

        if (isManualSource) {
          // Manual source: compute inter/intra from place_of_supply vs destination_of_supply
          const normalizeState = (state: string | null | undefined): string => {
            if (!state) return "";
            const normalized = (state || "").toString().trim().toUpperCase();
            if (normalized.length === 2) return normalized;
            try {
              return getStateAbbreviation(state) || normalized;
            } catch {
              return normalized;
            }
          };
          const placeOfSupply = normalizeState(
            invoiceDataForGST.place_of_supply,
          );
          const destinationOfSupply = normalizeState(
            invoiceDataForGST.destination_of_supply,
          );
          const hasSupplyInfo = placeOfSupply && destinationOfSupply;
          const isInterState =
            hasSupplyInfo && placeOfSupply !== destinationOfSupply;
          const specification = isInterState ? "inter" : "intra";
          if (mounted) await loadTaxSpecification(specification);
        } else {
          // Non-manual: use GST determination API
          const companyData = getCompany();
          const invoiceDataForGSTWithDest = {
            ...invoiceDataForGST,
            source_of_supply: invoiceDataForGST.place_of_supply,
            destination_of_supply: companyData?.state || "",
          };

          const gstResult = await determineGSTType(invoiceDataForGSTWithDest);
          if (mounted && gstResult) {
            const specification = gstResult.tax_type; // "inter" or "intra"
            await loadTaxSpecification(specification);
          }
        }
      } catch (error) {
        // Fallback to intra state taxes on error
        try {
          if (mounted) await loadTaxSpecification("intra");
        } catch (fallbackError) {}
      }
    };

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, [
    isDataLoading,
    isManualSource,
    initialInvoiceJson,
    fetchedInvoiceJson,
    loadTaxSpecification,
    determineGSTType,
  ]);
  // Note: Queries are automatically fetched by useQueriesBySource hook

  const handleSearchCustomerModalOpen = () => {
    setIsSearchCustomerModalOpen(true);
  };

  const handleQueryClick = (queryId: number) => {
    setSelectedQueryId(queryId);
    setIsQueryDetailModalOpen(true);
  };

  // Handle query modal opening
  const handleQueryRaiseModalOpen = () => {
    setIsQueryRaiseModalOpen(true);
  };

  // Validation handlers
  const handleValidationComplete = useCallback(
    async (result: any) => {
      let postValidationLineItems: any[] | null = null;
      try {
        setValidationData(result.data);
        const validationSummary = result.data?.validation_results;
        const passedValidations = validationSummary?.passed_validations || 0;
        const totalValidations = validationSummary?.total_validations || 0;

        // Check if all validations passed
        const allValidationsPassed = passedValidations === totalValidations;
        setValidationPassed(allValidationsPassed);

        // Do a full on check and keep updating the tax_id based on the response and set state !!
        const interIntraGst = validationSummary?.inter_intra_gst;

        if (interIntraGst?.details?.line_items_tax_ids) {
          const taxSpecification =
            invoiceJson?.gst_treatment === "overseas"
              ? "inter"
              : interIntraGst.details.tax_specification;
          const lineItemsTaxIds = interIntraGst.details.line_items_tax_ids;
          // Here based on tax_specification load taxes again => and update the taxes state
          await loadTaxSpecification(taxSpecification);

          // CRITICAL: Create the updated array ONCE and reuse it for both updates
          // This ensures both state updates use the exact same array reference
          const updateLineItemsWithTaxInfo = (items: any[]) => {
            const roundToTwoDecimals = (n: number): number =>
              Math.round((n + Number.EPSILON) * 100) / 100;
            const updatedItems = [...items];

            lineItemsTaxIds.forEach((taxIdInfo: any) => {
              const {
                index,
                tax_id,
                found,
                tax_percentage,
                tax_specification,
              } = taxIdInfo;

              // Only update if tax_id is found and the index is valid
              if (
                found &&
                tax_id &&
                index >= 0 &&
                index < updatedItems.length
              ) {
                // Skip items explicitly marked as NO_TAX or empty
                const currentItem = updatedItems[index];
                if (currentItem.tax_id === "NO_TAX") {
                  return;
                }

                // Convert tax_specification to tax_name format
                let taxName = "";
                if (tax_specification === "intra") {
                  taxName = `GST${tax_percentage || ""}`;
                } else if (tax_specification === "inter") {
                  taxName = `IGST${tax_percentage || ""}`;
                }

                const nextItem = {
                  ...currentItem,
                  tax_id: tax_id,
                  tax_percentage: tax_percentage,
                  tax_name: taxName,
                };

                // Compute tax_amount only when validation actually set a tax_id for this line.
                // If validation didn't set a tax_id for a line, leave tax_amount as-is.
                const rate = Number(nextItem?.rate ?? 0) || 0;
                const quantity = Number(nextItem?.quantity ?? 0) || 0;
                const taxable = rate * quantity;
                const pct = Number(nextItem?.tax_percentage ?? 0) || 0;
                const taxAmount =
                  String(nextItem?.tax_id ?? "") === "NO_TAX" ||
                  String(nextItem?.tax_id ?? "") === "" ||
                  pct === 0
                    ? 0
                    : roundToTwoDecimals((taxable * pct) / 100);

                updatedItems[index] = {
                  ...nextItem,
                  tax_amount: taxAmount,
                  item_total: taxable,
                  item_total_inclusive_of_tax: roundToTwoDecimals(
                    taxable + taxAmount,
                  ),
                };
              }
            });

            return updatedItems;
          };

          // Get current line items from invoiceJson (source of truth)
          const currentLineItems = invoiceJson?.line_items || lineItems;

          // Create updated array ONCE
          const updatedLineItems = updateLineItemsWithTaxInfo(currentLineItems);
          postValidationLineItems = updatedLineItems;

          // Snapshot baseline only on the first validation run; never overwrite after lock.
          if (!validationBaselineLockedRef.current) {
            validationBaselineLineItemsRef.current = updatedLineItems.map(
              (li) => ({
                ...li,
              }),
            );
          }

          // Update invoiceJson FIRST with the shared array reference
          setInvoiceJson((prevInvoiceJson) => {
            if (!prevInvoiceJson) return prevInvoiceJson;

            return {
              ...prevInvoiceJson,
              line_items: updatedLineItems, // Same array reference
            };
          });

          // Update lineItems with the SAME array reference
          // This ensures both states are perfectly in sync
          setLineItems([...updatedLineItems]);
        }

        // Extract failed validation messages
        const failedMessages =
          validationSummary?.validation_details
            ?.filter((validation: any) => !validation.valid)
            ?.map((validation: any) => validation.message) || [];

        setFailedValidationMessages(failedMessages);

        // Handle GL Classification
        const glClassification = validationSummary?.gl_classification;
        if (
          glClassification &&
          glClassification.details?.lines &&
          invoiceJson?.line_items
        ) {
          // Capture per-line gl_source_layer so the "Apply GL as default" modal
          // can later gate on "llm"-sourced items only.
          const sourceLayerMap: Record<number, string> = {};
          glClassification.details.lines.forEach((glLine: any) => {
            sourceLayerMap[glLine.index] = glLine.gl_source_layer || "";
          });
          setGlSourceLayerMap(sourceLayerMap);

          const jobid = invoiceDocument?.jobid;
          if (!jobid) return;

          // Check if user has skipped this document
          const skipKey = `gl_modal_skipped_${jobid}`;
          const isSkipped = localStorage.getItem(skipKey) === "true";

          if (isSkipped) {
            return; // Don't show modal if skipped
          }

          // Filter line items that meet the criteria
          const eligibleLineItems: GLClassificationLineItem[] = [];

          glClassification.details.lines.forEach((glLine: any) => {
            const lineItemIndex = glLine.index;
            const currentLineItem = invoiceJson.line_items[lineItemIndex];

            if (!currentLineItem) return;

            // Check conditions: gl_confidence > 0.85 AND gl_source_layer === 'knn' AND (account_name differs OR account_id differs)
            const meetsConfidence = glLine.gl_confidence > 0.85;
            const isKNN =
              glLine.gl_source_layer === "override" ||
              glLine.gl_source_layer === "knn";
            // const accountNameDiffers =
            //   currentLineItem.account_name !== glLine.account_name;
            const accountIdDiffers =
              currentLineItem.account_id !== glLine.account_id;

            if (meetsConfidence && isKNN && accountIdDiffers) {
              eligibleLineItems.push({
                index: lineItemIndex,
                description:
                  currentLineItem.description || currentLineItem.name || "",
                oldAccountId: currentLineItem.account_id || "",
                oldAccountName: currentLineItem.account_name || "",
                newAccountId: glLine.account_id,
                newAccountName: glLine.account_name,
                confidence: glLine.gl_confidence,
              });
            }
          });

          // Show modal if there are eligible line items
          if (eligibleLineItems.length > 0) {
            setGLClassificationLineItems(eligibleLineItems);
            setIsGLClassificationModalOpen(true);
          }
        }
      } finally {
        lineItemTrackingAfterValidationRef.current = true;
        validationBaselineLockedRef.current = true;
        seedTrackedUserLineItemChangesAfterValidation(
          postValidationLineItems ??
            invoiceJsonRef.current?.line_items ??
            lineItems,
        );
      }
    },
    [
      invoiceJson,
      lineItems,
      invoiceDocument,
      loadTaxSpecification,
      seedTrackedUserLineItemChangesAfterValidation,
      // Note: State setters (setValidationData, setValidationPassed, etc.) are stable
      // and don't need to be in dependencies, but including them is harmless
    ],
  );

  // Handle GL Classification Apply
  const handleGLClassificationApply = (
    selectedLineItems: GLClassificationLineItem[],
  ) => {
    if (!invoiceJson) return;

    if (selectedLineItems.length === 0) {
      setIsGLClassificationModalOpen(false);
      setGLClassificationLineItems([]);
      return;
    }

    const updatedLineItems = [...invoiceJson.line_items];
    const confirmedIndexes: number[] = [];

    selectedLineItems.forEach((item) => {
      if (updatedLineItems[item.index]) {
        updatedLineItems[item.index] = {
          ...updatedLineItems[item.index],
          account_id: item.newAccountId,
          account_name: item.newAccountName,
          gl_confidence: item.confidence,
        };
        confirmedIndexes.push(item.index);
      }
    });

    const updated = { ...invoiceJson, line_items: updatedLineItems };
    setInvoiceJson(updated);
    setLineItems(updatedLineItems);
    // Applying the previous GL change counts as the reviewer confirming these
    // lines' GL, so the inline Credit A/c picker stops showing "Select account"
    // for them (mirrors handleInlineGlSelect + handleSaveLineItem).
    setGlConfirmedLines((prev) => {
      const next = { ...prev };
      confirmedIndexes.forEach((idx) => {
        next[idx] = true;
      });
      return next;
    });
    // if (!isPostedToZoho && canUpdate) {
    //   triggerAutoSave(updated);
    // }

    setIsGLClassificationModalOpen(false);
    setGLClassificationLineItems([]);
  };

  // Handle GL Classification Skip
  const handleGLClassificationSkip = () => {
    const jobid = invoiceDocument?.jobid;
    if (jobid) {
      const skipKey = `gl_modal_skipped_${jobid}`;
      localStorage.setItem(skipKey, "true");
    }
    setIsGLClassificationModalOpen(false);
    setGLClassificationLineItems([]);
  };

  // Validation function to check line items have tax information
  const validateLineItemsTaxInfo = (): {
    isValid: boolean;
    errorMessage?: string;
  } => {
    if (!lineItems || lineItems.length === 0) {
      return { isValid: true }; // No line items to validate
    }

    const invalidItems = lineItems.filter((item, index) => {
      // Check if either tax_id or tax_percentage is provided
      const hasTaxId = item.tax_id && item.tax_id !== "";
      const hasTaxPercentage =
        item.tax_percentage !== null && item.tax_percentage !== undefined;

      return !hasTaxId && !hasTaxPercentage;
    });

    if (invalidItems.length > 0) {
      const itemNumbers = invalidItems.map((_, index) => index + 1).join(", ");
      return {
        isValid: false,
        errorMessage: `Line items ${itemNumbers} are missing tax information. Please ensure each line item has either a Tax ID or Tax Percentage before approving.`,
      };
    }

    return { isValid: true };
  };

  /**
   * A line item's account name is considered manually edited when trackedChanges
   * has an entry with exactly the `Line Item N: Account Name` label. Edits to
   * other fields (quantity, rate, etc.) do not disqualify the line.
   */
  const isAccountNameEditedForLineItem = useCallback(
    (idx: number): boolean =>
      trackedChanges.some(
        (c) => c.field === `Line Item ${idx + 1}: Account Name`,
      ),
    [trackedChanges],
  );

  /**
   * Build the eligible list of line items for the "Apply this GL automatically
   * next time?" modal. A line item is eligible when it has a non-empty GL and
   * the user has not edited the account name.
   */
  const buildApplyGLAsDefaultItems =
    useCallback((): ApplyGLAsDefaultLineItem[] => {
      return lineItems
        .map((li, idx) => ({ li, idx }))
        .filter(
          ({ li, idx }) =>
            !!li.account_id &&
            !!li.account_name &&
            !isAccountNameEditedForLineItem(idx) &&
            (glSourceLayerMap[idx] === "llm" ||
              glSourceLayerMap[idx] === "knn"),
        )
        .map(({ li, idx }) => ({
          lineItemIndex: idx,
          description: li.description || li.name || "",
          accountId: li.account_id || "",
          accountName: li.account_name || "",
          accountCode: (li as any).account_code,
        }));
    }, [lineItems, isAccountNameEditedForLineItem, glSourceLayerMap]);

  const handleApplyGLAsDefaultSkip = () => {
    setIsApplyGLAsDefaultOpen(false);
    setApplyGLAsDefaultItems([]);
    handleDocumentApproval({ skipApplyGLPrompt: true });
  };

  const handleApplyGLAsDefaultSave = async () => {
    const itemsToSave = applyGLAsDefaultItems;
    if (itemsToSave.length === 0) {
      setIsApplyGLAsDefaultOpen(false);
      handleDocumentApproval({ skipApplyGLPrompt: true });
      return;
    }

    setIsApplyingGLDefaults(true);
    try {
      const requests: SemanticOverrideRequest[] = itemsToSave.map((item) => {
        const li = lineItems[item.lineItemIndex];
        return {
          line_item: {
            vendor: "",
            vendor_id: "",
            description: li?.description || "",
            product_description: li?.description || "",
            hsn: li?.hsn_or_sac || "",
            hsn_description: (li as any)?.hsn_description || "",
            customer: invoiceJson?.customer_name || "Unknown Customer",
            customer_id: invoiceJson?.customer_id || "",
          },
          // gl_id: item.accountId,
          gl_id: item.accountName,
          similarity_threshold: 0.85,
          document_type: "invoice",
          account_id: item.accountId,
          note: "Save GL as default for similar future invoices",
        };
      });

      // Use Promise.all with per-request catch so a single failure does not
      // reject the whole batch - we still want to continue with approval.
      const results = await Promise.all(
        requests.map((r) =>
          createSemanticOverride(r).catch((err: unknown) => {
            console.error("Failed to save GL default:", err);
            return { success: false } as { success: false };
          }),
        ),
      );

      const failures = results.filter((r) => !r?.success).length;
      const total = results.length;
      if (failures === total) {
        showError("Could not save GL defaults. Continuing with approval.");
      } else if (failures > 0) {
        showWarning(
          `Saved ${total - failures} of ${total} GL defaults. Continuing with approval.`,
        );
      }
    } finally {
      setIsApplyingGLDefaults(false);
      setIsApplyGLAsDefaultOpen(false);
      setApplyGLAsDefaultItems([]);
      handleDocumentApproval({ skipApplyGLPrompt: true });
    }
  };

  const handleApproveClick = () => {
    // Block approval while the customer-change pipeline is re-running
    if (isRecomputing) {
      return;
    }

    if (exchangeRateLoading) {
      showError("Wait for the exchange rate to finish loading.");
      return;
    }

    // Block approval until every LLM-classified line has an actively chosen GL
    // (Credit A/c). Only enforced when the inline picker is shown (editable review).
    if (inlineGlPickerActive) {
      const unpicked = lineItems
        .map((_: any, idx: number) => idx)
        .filter((idx: number) => lineNeedsGlPick(idx));
      if (unpicked.length > 0) {
        const nums = unpicked.map((i: number) => i + 1).join(", ");
        setApiErrors(
          new Error(
            `Select a GL (Credit A/c) for line item${
              unpicked.length > 1 ? "s" : ""
            } ${nums} before approving.`,
          ),
        );
        setOpenInlineGlIndex(unpicked[0]);
        return;
      }
    }

    // Run validation first
    if (!validateInvoice()) {
      // ErrorBanner will display all validation errors
      return;
    }

    // Check if there are any failed AI validations
    const hasFailedValidations =
      failedValidationMessages.length > 0 ||
      !validationPassed ||
      validationData?.validation_summary?.failed_validations > 0;

    if (hasFailedValidations && !isManualSource) {
      setIsValidationProgressModalOpen(true);
      return;
    }

    // If no validation failures, proceed with approval
    handleDocumentApproval();
  };

  const handleCloseValidationModal = () => {
    setIsValidationProgressModalOpen(false);
  };

  // New handler for continuing with approval despite validation failures
  const handleContinueApproval = () => {
    setIsValidationProgressModalOpen(false);
    // Proceed with approval despite validation failures
    handleDocumentApproval();
  };

  // Helper function to extract meaningful error message
  // Main approval workflow function
  const handleDocumentApproval = async (
    opts: {
      skipApplyGLPrompt?: boolean;
      skipReviewChangesModal?: boolean;
      approvalAudit?: ApprovalAuditPayload;
    } = {},
  ) => {
    if (!documentId || !invoiceJson) {
      console.error("Missing documentId or invoiceJson for approval");
      return;
    }

    // Offer to memorize untouched GLs as defaults for similar future invoices.
    // The modal handlers re-call this function with skipApplyGLPrompt to avoid
    // re-prompting after the user chose "Not now" or "Save as default".
    if (!opts.skipApplyGLPrompt && !isPostedToZoho) {
      const eligible = buildApplyGLAsDefaultItems();
      if (eligible.length > 0) {
        setApplyGLAsDefaultItems(eligible);
        setIsApplyGLAsDefaultOpen(true);
        return;
      }
    }

    // CRITICAL: Don't allow approval if data is still loading
    if (isDataLoading) {
      setApiErrors(new Error("Data is still loading. Please wait."));
      return;
    }

    if (
      exchangeRateRequired &&
      isExchangeRateMissingForTallyPost(invoiceJson.exchange_rate, {
        userSet: invoiceJson.exchange_rate_user_set,
        isPosted: isPostedToZoho,
      })
    ) {
      setFieldErrors({
        exchange_rate:
          "Exchange rate is required for foreign currency documents when posting to Tally.",
      });
      return;
    }

    // Check permissions
    if (isPostedToZoho) {
      if (!canUpdateAfterPush) {
        setApiErrors(
          new Error(
            getUpdateDocumentAfterPushPermissionDeniedMessage(
              "invoice",
              accountingBackend,
            ),
          ),
        );
        return;
      }
    } else {
      if (!canPushToZoho) {
        setApiErrors(
          new Error(
            getPushPermissionDeniedMessage("invoices", accountingBackend),
          ),
        );
        return;
      }
    }

    // Closed month: non-Admin/CFO cannot approve/update directly; show confirm modal to submit for review
    if (isMonthClosed && !isAdminOrCFO) {
      setPendingClosedMonthSource("approve");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // PRIORITY 0: Manual draft not posted + create requires review → create review flow (incl. after rejection resubmission)
    if (
      !isPostedToZoho &&
      isManualSource &&
      canCreate &&
      needsReviewForCreate
    ) {
      const createChanges: ChangeDetails = {
        reason: "Requesting permission to create invoice",
        field_changes: trackedChanges.length > 0 ? trackedChanges : undefined,
        raw_json: invoiceJsonRef.current || invoiceJson,
        original_json: originalInvoiceJson ?? undefined,
        action_types: ["create"],
      };
      setPendingReviewAction({
        action: () => handleDocumentApproval({ skipApplyGLPrompt: true }),
        changeDetails: createChanges,
        disableSubmit: false,
        actionCode: ACTION_CODES.CREATE,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // PRIORITY 1: Check if there's a pending GL override that needs review
    if (
      pendingGLOverrideReview.length > 0 &&
      needsReviewForOverrideGLPermanent
    ) {
      // Check if there's a pending review (block if exists)
      if (review?.status === "pending") {
        setApiErrors(
          new Error(
            "A review request is already pending for this document. Please wait for it to be reviewed.",
          ),
        );
        return;
      }

      // Collect all GL overrides that need review
      const glOverridesNeedingReview = pendingGLOverrideReview.filter(
        (gl) => gl.changeScope === "always",
      );

      if (glOverridesNeedingReview.length > 0) {
        // Build field_changes for all GL overrides
        const glFieldChanges = glOverridesNeedingReview.map((gl) => ({
          field: `Line Item ${gl.lineItemIndex + 1}: Account Name`,
          old: getAccountNameLeaf(gl.glData.old_account_name),
          new: getAccountNameLeaf(gl.glData.new_account_name),
        }));

        // Store all GL override data in array format
        const lineItemGLDataArray = glOverridesNeedingReview.map((gl) => ({
          line_item_index: gl.lineItemIndex,
          old_account_id: gl.glData.old_account_id,
          old_account_name: gl.glData.old_account_name,
          new_account_id: gl.glData.new_account_id,
          new_account_name: gl.glData.new_account_name,
          change_scope: gl.changeScope,
          reason: gl.reason,
          override_data: gl.overrideData,
        }));

        // Send review as override_gl_permanent
        const glChanges: ChangeDetails = {
          reason: glOverridesNeedingReview[0].reason, // Use first reason as primary
          old_value: glOverridesNeedingReview[0].glData.old_account_name,
          new_value: glOverridesNeedingReview[0].glData.new_account_name,
          // Include GL details in field_changes for display
          field_changes: glFieldChanges,
          // Store line item index and GL data for applying changes
          line_item_index: glOverridesNeedingReview[0].lineItemIndex, // Keep for backward compatibility
          line_item_gl_data: lineItemGLDataArray as any, // Store as array
          change_scope: glOverridesNeedingReview[0].changeScope, // Store whether it's "always" or "once"
          // Include raw JSON with ALL changes (GL + other changes)
          raw_json: invoiceJsonRef.current || invoiceJson,
          original_json: originalInvoiceJson ?? undefined,
          validation_snapshot: buildValidationSnapshotForReview(
            validationBaselineLineItemsRef.current,
            invoiceJsonRef.current || invoiceJson,
          ),
          // Set action types - always include override_gl_permanent
          action_types: ["override_gl_permanent"],
          override_data: glOverridesNeedingReview[0].overrideData, // Use first one for backward compatibility
        };

        // Check if push_to_zoho/update_after_push also needs review
        const needsOtherReview = isPostedToZoho
          ? needsReviewForUpdateAfterPush
          : needsReviewForPushToZoho;

        // Check if edit also needs review (only for non-posted documents)
        // BUT only if push_to_zoho does NOT need review (push_to_zoho has priority)
        const needsEditReview =
          !isPostedToZoho && needsReviewForEdit && !needsReviewForPushToZoho;

        // Build action_types array
        const actionTypes: string[] = ["override_gl_permanent"];

        if (needsOtherReview) {
          if (isPostedToZoho) {
            actionTypes.push("update_after_push");
          } else {
            actionTypes.push("push_to_zoho");
          }
        } else if (needsEditReview) {
          // Only add edit if push_to_zoho doesn't need review
          actionTypes.push("edit");
        }

        glChanges.action_types = actionTypes;

        // If other action also needs review OR edit needs review, merge tracked changes
        if (needsOtherReview || needsEditReview) {
          // Merge other tracked changes into field_changes if they exist
          // This ensures the reviewer sees both GL overrides and other changes
          // BUT filter out GL override changes (Line Item X: Account Name) since we already added them explicitly
          if (trackedChanges.length > 0) {
            // Get all line item numbers that have GL overrides
            const glOverrideLineItemNumbers = glOverridesNeedingReview.map(
              (gl) => gl.lineItemIndex + 1,
            );
            const glOverrideFieldPatterns = glOverrideLineItemNumbers.map(
              (num) => `Line Item ${num}: Account Name`,
            );

            // Filter out all GL override changes from trackedChanges to prevent duplicates
            const otherTrackedChanges = trackedChanges.filter(
              (change) => !glOverrideFieldPatterns.includes(change.field),
            );

            glChanges.field_changes = [
              ...(glChanges.field_changes || []),
              ...otherTrackedChanges,
            ];
          }
        }

        setPendingReviewAction({
          action: () => {
            // After review is approved, proceed with approval
            handleDocumentApproval({ skipApplyGLPrompt: true });
          },
          changeDetails: glChanges,
          disableSubmit: false,
        });
        setIsReviewModalOpen(true);
        return;
      }
    }

    // PRIORITY 2: Check if push_to_zoho/update_after_push needs review
    const needsReviewForPush = isPostedToZoho
      ? needsReviewForUpdateAfterPush
      : needsReviewForPushToZoho;

    if (needsReviewForPush) {
      // Check if there are tracked changes
      if (trackedChanges.length === 0 && isPostedToZoho) {
        // No changes - show modal but disable submit
        const allChanges: ChangeDetails = {
          reason:
            "No changes detected. Please make changes to the document before requesting a review.",
          field_changes: [],
          raw_json: invoiceJsonRef.current || invoiceJson,
          action_types: [isPostedToZoho ? "update_after_push" : "push_to_zoho"],
        };

        setPendingReviewAction({
          action: () => {
            // Do nothing - submit will be disabled
          },
          changeDetails: allChanges,
          disableSubmit: true,
        });
        setIsReviewModalOpen(true);
        return;
      }

      // There are changes - show review modal with changes
      const companyDataForReview = getCompany();
      const currentWithDestination = applyExchangeRateForAccountingPayload(
        {
          ...invoiceJson,
          destination_of_supply: companyDataForReview?.state
            ? getStateAbbreviation(companyDataForReview.state)
            : (invoiceJson?.destination_of_supply ?? ""),
        },
        accountingBackend,
        exchangeRatePostContext,
      );
      let rawJsonForReview: any = { documentId, ...currentWithDestination };
      const allChanges: ChangeDetails = {
        reason: isPostedToZoho
          ? getReviewRequestUpdateAfterPostReason("invoice", accountingBackend)
          : getReviewRequestPushReason("invoice", accountingBackend),
        field_changes: trackedChanges.length > 0 ? trackedChanges : undefined,
        raw_json: rawJsonForReview,
        // original_json: isPostedToZoho
        //   ? (originalInvoiceJson ?? undefined)
        //   : undefined,
        original_json: originalInvoiceJson ?? undefined,
        validation_snapshot: buildValidationSnapshotForReview(
          validationBaselineLineItemsRef.current,
          invoiceJsonRef.current || invoiceJson,
        ),
        action_types: [isPostedToZoho ? "update_after_push" : "push_to_zoho"],
      };

      setPendingReviewAction({
        action: () => {
          // After review is approved, proceed with approval
          handleDocumentApproval({ skipApplyGLPrompt: true });
        },
        changeDetails: allChanges,
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // PRIORITY 3: Check if edit needs review (only if NOT posted AND user made changes)
    // Only check if push_to_zoho doesn't require review AND no GL override needs review
    if (
      !isPostedToZoho &&
      trackedChanges.length > 0 &&
      needsReviewForEdit &&
      !needsReviewForPushToZoho && // Only if push_to_zoho doesn't need review
      !(pendingGLOverrideReview.length > 0 && needsReviewForOverrideGLPermanent) // Only if GL override doesn't need review
    ) {
      // Create review request with "edit" action (no GL override, already handled in PRIORITY 1)
      // Include original_json so review modal and approval panels show "Document (before changes)" + "Changes Requested" like update_after_push
      const editChanges: ChangeDetails = {
        reason: "Requesting permission to edit invoice",
        field_changes: trackedChanges,
        raw_json: invoiceJsonRef.current || invoiceJson,
        original_json: originalInvoiceJson ?? undefined,
        validation_snapshot: buildValidationSnapshotForReview(
          validationBaselineLineItemsRef.current,
          invoiceJsonRef.current || invoiceJson,
        ),
        action_types: ["edit"],
      };

      setPendingReviewAction({
        action: () => handleDocumentApproval({ skipApplyGLPrompt: true }), // Retry after approval
        changeDetails: editChanges,
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    if (!opts.skipReviewChangesModal) {
      const hasDirectApproveChanges =
        trackedChanges.length > 0 || pendingGLOverrideReview.length > 0;

      if (hasDirectApproveChanges) {
        const directApproveChanges: ChangeDetails = {
          field_changes: trackedChanges.length > 0 ? trackedChanges : undefined,
          raw_json: invoiceJsonRef.current || invoiceJson,
          original_json: originalInvoiceJson ?? undefined,
          validation_snapshot: buildValidationSnapshotForReview(
            validationBaselineLineItemsRef.current,
            invoiceJsonRef.current || invoiceJson,
          ),
          action_types: [isPostedToZoho ? "update_after_push" : "push_to_zoho"],
        };

        setPendingReviewAction({
          requiresReview: false,
          action: () =>
            handleDocumentApproval({
              skipApplyGLPrompt: true,
              skipReviewChangesModal: true,
            }),
          changeDetails: directApproveChanges,
          disableSubmit: false,
          actionCode: isPostedToZoho
            ? ACTION_CODES.UPDATE_AFTER_PUSH
            : ACTION_CODES.PUSH_TO_ZOHO,
        });
        setIsReviewModalOpen(true);
        return;
      }
    }

    try {
      setIsApprovalLoading(true);

      // Get company data to set destination_of_supply
      const companyData = getCompany();
      const invoiceDataWithDestination = applyExchangeRateForAccountingPayload(
        {
          ...invoiceJson!,
          destination_of_supply: companyData?.state
            ? getStateAbbreviation(companyData.state)
            : invoiceJson!.destination_of_supply || "",
        },
        accountingBackend,
        exchangeRatePostContext,
      );

      // Manual draft not yet pushed: use push-draft endpoint (no PDF required)
      if (!isPostedToZoho && isManualSource && documentId) {
        if (!canPushToZoho) {
          throw new Error(
            getPushPermissionDeniedMessage("invoices", accountingBackend),
          );
        }
        const companyGstEnabled = Number(companyData?.companyGstEnabled ?? 0);
        const built = buildManualInvoiceJson(
          invoiceDataWithDestination,
          invoiceDataWithDestination.line_items ?? [],
          {
            currencies: [],
            companyGstEnabled,
            exchangeRateInput: showExchangeRateField
              ? String(invoiceDataWithDestination.exchange_rate ?? "")
              : "1",
          },
        );
        if (!built.success) {
          throw new Error(Object.values(built.errors).join(" "));
        }
        const canonicalInvoiceJson = built.data;
        await pushManualInvoiceDraft(
          parseInt(documentId),
          companyGstEnabled,
          canonicalInvoiceJson,
          opts.approvalAudit,
        );
        await processInvoiceJsonHook(canonicalInvoiceJson);
        showSuccess("Invoice created successfully");
        sessionStorage.removeItem("autoSelectCustomer");
        navigate("/dashboard/invoices");
        return;
      }

      // Fetch fresh signed URL for PDF attachment (upload flow only)
      let pdfUrl = signedUrl;
      if (documentId && !isPostedToZoho && !isManualSource) {
        try {
          const signedUrlResponse = await getSignedUrlForDocument({
            documentId: parseInt(documentId),
            documentType: "invoice",
            expiration: 3600,
          });
          if (signedUrlResponse.success && signedUrlResponse.data.signedUrl) {
            pdfUrl = signedUrlResponse.data.signedUrl;
          }
        } catch (error) {}
      }

      // if (!isPostedToZoho && !pdfUrl) {
      //   throw new Error("PDF URL is required for invoice creation");
      // }

      // Step 1: Create or Update Zoho invoice based on edit mode
      if (isPostedToZoho) {
        // Send full invoice state to backend (Option 1: no delta); backend uses it as source of truth
        await updateZohoInvoice(
          parseInt(documentId),
          invoiceDataWithDestination,
          opts.approvalAudit,
        );
      } else {
        // Check permission before creating in Zoho
        if (!canPushToZoho) {
          throw new Error(
            getPushPermissionDeniedMessage("invoices", accountingBackend),
          );
        }
        // Error will be thrown automatically if it fails
        await createZohoInvoice(
          parseInt(documentId),
          invoiceDataWithDestination,
          pdfUrl!,
          opts.approvalAudit,
        );
      }

      // Step 2: Process invoice JSON for GL classification
      await processInvoiceJsonHook(invoiceDataWithDestination);

      const successMessage = isPostedToZoho
        ? "Invoice updated successfully"
        : "Invoice created successfully";
      showSuccess(successMessage);

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectCustomer");

      // Step 3: Navigate back to invoices
      navigate("/dashboard/invoices");
    } catch (error) {
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Handle reject invoice (direct or open review modal if review required)
  const handleRejectInvoice = async () => {
    if (!documentId) {
      console.error("Missing documentId for rejection");
      return;
    }

    // If user has permission but action requires review, open ReviewRequestModal
    if (canReject && needsReviewForReject) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.REJECT,
        changeDetails: {
          action_types: ["reject"],
          raw_json: invoiceJson ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    try {
      setIsApprovalLoading(true);

      const result = await rejectInvoiceHook(parseInt(documentId));
      if (!result) {
        throw new Error("Failed to reject invoice");
      }

      // Show success message
      showSuccess("Invoice rejected successfully");

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectCustomer");

      // Navigate back to invoices
      navigate("/dashboard/invoices");
    } catch (error) {
      console.error("Error rejecting invoice:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Handle mark as duplicate (direct or open review modal if review required)
  const handleMarkAsDuplicate = async () => {
    if (!documentId) {
      console.error("Missing documentId for marking as duplicate");
      return;
    }

    // If user has permission but action requires review, open ReviewRequestModal
    if (canMarkAsDuplicate && needsReviewForDuplicate) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.DUPLICATE,
        changeDetails: {
          action_types: ["duplicate"],
          raw_json: invoiceJson ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    try {
      setIsApprovalLoading(true);

      const result = await markAsDuplicateHook(parseInt(documentId));
      if (!result) {
        throw new Error("Failed to mark invoice as duplicate");
      }

      // Show success message
      showSuccess("Invoice marked as duplicate successfully");

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectCustomer");

      // Navigate back to invoices section
      navigate("/dashboard/invoices");
    } catch (error) {
      console.error("Error marking invoice as duplicate:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Handle delete (posted invoice only): open review modal if required, else call API
  const handleDeleteInvoice = async () => {
    if (!documentId) return;
    if (needsReviewForDelete) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO,
        changeDetails: {
          action_types: ["delete_after_push_to_zoho"],
          raw_json: invoiceJson ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }
    try {
      setIsApprovalLoading(true);
      const ok = await deletePostedInvoiceHook(parseInt(documentId));
      if (!ok) throw new Error("Failed to delete invoice");
      showSuccess(
        getDeletedFromBackendSuccessMessage("Invoice", accountingBackend),
      );
      sessionStorage.removeItem("autoSelectCustomer");
      navigate("/dashboard/invoices");
    } catch (error) {
      console.error("Error deleting invoice:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Cancel write off (from review page banner): use reviewData permission + review flow, then API
  const handleCancelWriteOff = useCallback(async () => {
    if (!documentId) return;
    if (!canCancelWriteOff) {
      showError("You do not have permission to cancel write off.");
      return;
    }
    if (needsReviewForCancelWriteOff) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.CANCEL_WRITE_OFF,
        changeDetails: {
          action_types: ["cancel_write_off"],
          raw_json: invoiceJson ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }
    setCancelWriteOffLoading(true);
    try {
      await cancelWriteOffInvoice(parseInt(documentId));
      setLocalWriteOffStatus("none");
      showSuccess("Write off cancelled successfully");
    } catch (error) {
      console.error("Error cancelling write off:", error);
      showError("Failed to cancel write off");
    } finally {
      setCancelWriteOffLoading(false);
    }
  }, [
    documentId,
    canCancelWriteOff,
    needsReviewForCancelWriteOff,

    invoiceJson,
    showError,
    showSuccess,
  ]);

  // Note: Query creation is now handled by QueryRaiseModal with clean architecture hooks
  // onOpenChange callback will trigger refetchQueries automatically

  // Line item handlers
  const handleEditLineItem = (index: number) => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be edited while validation is running.");
      return;
    }
    setEditingLineItemIndex(index);
    const lineItemData = { ...lineItems[index] };

    // Store the ORIGINAL account name and id BEFORE any modifications
    setOriginalAccountName(lineItemData.account_name || "");
    setOriginalAccountId(lineItemData.account_id || "");

    // Ensure tax_percentage is set with a default value if not present
    if (
      lineItemData.tax_percentage === undefined ||
      lineItemData.tax_percentage === null
    ) {
      lineItemData.tax_percentage = 0; // Default to 12%
    }

    // If tax_id is not set (or explicitly NO_TAX) and tax_percentage is available and > 0, try to find matching tax
    if (
      (!lineItemData.tax_id || lineItemData.tax_id === "") &&
      lineItemData.tax_percentage !== undefined &&
      lineItemData.tax_percentage !== null &&
      lineItemData.tax_percentage > 0
    ) {
      const matchingTax = taxes.find(
        (tax) => parseFloat(tax.tax_percentage) === lineItemData.tax_percentage,
      );
      if (matchingTax) {
        lineItemData.tax_id = matchingTax.tax_id;
      }
    }

    // Note: Account verification is now handled in EditLineItemForInvoiceComponent

    setEditingLineItemData(lineItemData);
    setIsEditLineItemModalOpen(true);
  };

  const handleDeleteLineItem = (index: number) => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be deleted while validation is running.");
      return;
    }
    const updatedLineItems = lineItems.filter((_, i) => i !== index);
    setLineItems(updatedLineItems);
    // Update invoiceJson with the new line items and recalculated totals
    updateLineItems(updatedLineItems);
  };

  const handleAddLineItem = () => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be added while validation is running.");
      return;
    }
    // Create empty line item template with all required fields
    const newLineItem: LineItemEntity = {
      name: "",
      description: "",
      quantity: 0,
      rate: 0,
      unit: "" as any,
      hsn_or_sac: "",
      hsn_description: "",
      sku: "",
      tax_id: "",
      tax_name: "",
      tax_percentage: 0,
      tax_amount: 0,
      account_id: "",
      account_name: "",
      item_total: 0,
      item_total_inclusive_of_tax: 0,
      gl_confidence: 0,
      item_order: lineItems.length + 1,
      is_billable: true,
    };

    setEditingLineItemIndex(-1); // Use -1 to indicate new item
    setEditingLineItemData(newLineItem);
    setOriginalAccountName(""); // No original since it's new
    setOriginalAccountId(""); // No original since it's new
    setIsEditLineItemModalOpen(true);
  };

  const handleSaveLineItem = () => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be saved while validation is running.");
      return;
    }
    if (editingLineItemData) {
      // Check if account_name was changed from original
      const accountNameChanged =
        editingLineItemIndex !== -1 &&
        editingLineItemIndex !== null &&
        originalAccountId !== "" &&
        editingLineItemData.account_id !== originalAccountId;

      // If account_name was changed, set gl_confidence to 0
      const lineItemToSave = accountNameChanged
        ? { ...editingLineItemData, gl_confidence: 0 }
        : editingLineItemData;

      if (editingLineItemIndex === -1) {
        // Adding new item
        const updatedLineItems = [...lineItems, lineItemToSave];
        setLineItems(updatedLineItems);
        updateLineItems(updatedLineItems);
        showSuccess("Line item added successfully");
      } else if (editingLineItemIndex !== null) {
        // Editing existing item
        const updatedLineItems = [...lineItems];
        updatedLineItems[editingLineItemIndex] = lineItemToSave;
        setLineItems(updatedLineItems);
        updateLineItems(updatedLineItems);
        // A modal save counts as the reviewer confirming this line's GL, so the
        // inline Credit A/c picker won't keep demanding a pick for it.
        setGlConfirmedLines((p) => ({ ...p, [editingLineItemIndex]: true }));
        showSuccess("Line item updated successfully");
      }

      // Close modal and reset
      setIsEditLineItemModalOpen(false);
      setEditingLineItemIndex(null);
      setEditingLineItemData(null);

      // Show Update GL Dialog if:
      // 1. Account name was changed
      // 2. There are more than 1 line item (check after update)
      // 3. This was an existing item (not a new one)
      // const finalLineItemsCount =
      //   editingLineItemIndex === -1 ? lineItems.length + 1 : lineItems.length;
      // if (
      //   accountNameChanged &&
      //   finalLineItemsCount > 1 &&
      //   editingLineItemIndex !== -1 &&
      //   editingLineItemIndex !== null
      // ) {
      //   setPendingGLUpdate({
      //     changedLineItemIndex: editingLineItemIndex,
      //     oldAccountName: originalAccountName,
      //     newAccountName: editingLineItemData.account_name || "",
      //     newAccountId: editingLineItemData.account_id || "",
      //   });
      //   setIsUpdateGLDialogOpen(true);
      // }
    }
  };

  const handleCancelEdit = () => {
    setIsEditLineItemModalOpen(false);
    setEditingLineItemIndex(null);
    setEditingLineItemData(null);
  };

  // Handle applying GL to selected line items
  const handleApplyGLToSelected = (selectedIndices: number[]) => {
    if (!pendingGLUpdate) return;

    const updatedLineItems = [...lineItems];
    selectedIndices.forEach((index) => {
      if (updatedLineItems[index]) {
        updatedLineItems[index] = {
          ...updatedLineItems[index],
          account_id: pendingGLUpdate.newAccountId,
          account_name: pendingGLUpdate.newAccountName,
          gl_confidence: 0, // Mark as manually edited
        };
      }
    });

    setLineItems(updatedLineItems);
    updateLineItems(updatedLineItems);
    showSuccess(
      `GL updated for ${selectedIndices.length} line item${
        selectedIndices.length > 1 ? "s" : ""
      }`,
    );

    setIsUpdateGLDialogOpen(false);
    setPendingGLUpdate(null);
  };

  // Handle skipping GL update
  const handleSkipGLUpdate = () => {
    setIsUpdateGLDialogOpen(false);
    setPendingGLUpdate(null);
  };

  // Handle line item data changes from the component
  const handleLineItemDataChange = (newData: any) => {
    console.log("New data: Line Item Data Change", newData);
    setEditingLineItemData(newData);
  };

  // Handler functions for all sections
  const handleBasicInfoSave = (newData: any) => {
    // Update invoiceJson with basic info changes
    if (newData.invoiceNumber !== undefined) {
      // The AI Detection panel's Document Number Check validates the SAVED
      // number (DB), so after an on-screen edit its result is stale — flag the
      // edit so the panel hides that check and shows a note instead.
      if (newData.invoiceNumber !== invoiceJson?.invoice_number) {
        setInvoiceNumberEdited(true);
      }
      updateInvoiceJson({ invoice_number: newData.invoiceNumber });
    }
    if (newData.currency) {
      const updates: any = {
        currency_code: newData.currency,
      };
      // Also update currency_id and currency_symbol if provided
      if (newData.currency_id) {
        updates.currency_id = newData.currency_id;
      }
      if (newData.currency_symbol) {
        updates.currency_symbol = newData.currency_symbol;
      }
      updateInvoiceJson(updates);
    }
    if (showExchangeRateField) {
      const trimmed = String(newData.exchange_rate ?? "").trim();
      if (trimmed === "") {
        updateInvoiceJson({
          exchange_rate: null,
          exchange_rate_user_set: false,
        } as Partial<InvoiceDataEntity> & {
          exchange_rate: null;
          exchange_rate_user_set: false;
        });
      } else {
        const n = parseFloat(trimmed);
        if (Number.isFinite(n) && n > 0) {
          updateInvoiceJson({
            exchange_rate: n,
            exchange_rate_user_set: true,
          });
        }
      }
    }
  };

  const handleDatesInfoSave = (newData: any) => {
    // Update invoiceJson with date changes
    if (newData.documentDate !== undefined) {
      updateInvoiceJson({
        date: newData.documentDate,
        invoice_date: newData.documentDate,
      });
    }
    if (newData.dueDate !== undefined) {
      updateInvoiceJson({ due_date: newData.dueDate });
    }
  };

  const handleTaxInfoSave = (newData: any) => {
    // Update invoiceJson with tax info changes
    if (newData.vendorGstin !== undefined) {
      updateInvoiceJson({ vendor_gstin: newData.vendorGstin });
    }
    if (newData.customerGstin !== undefined) {
      updateInvoiceJson({
        customer_gstin: newData.customerGstin,
        gst_no: newData.customerGstin,
      });
    }
  };

  const handleDocumentMetadataSave = (newData: any) => {
    // Update invoiceJson with document metadata changes
    // This would depend on what fields are available in the metadata
  };

  const handleAmountSummarySave = (newData: any) => {
    // Update invoiceJson with amount changes
    // Note: Discount is already handled via onDiscountChange callback
    const parseAmountField = (value: string | undefined) => {
      const trimmed = String(value ?? "").trim();
      if (!trimmed) return 0;
      const parsed = parseFloat(trimmed.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(parsed) ? parsed : 0;
    };
    if (newData.subtotal !== undefined) {
      updateInvoiceJson({ sub_total: parseAmountField(newData.subtotal) });
    }
    if (newData.gstValue !== undefined) {
      updateInvoiceJson({ tax_total: parseAmountField(newData.gstValue) });
    }
    if (newData.totalAmount !== undefined) {
      updateInvoiceJson({ total: parseAmountField(newData.totalAmount) });
    }
  };

  // Show loader while fetching all required data (permissions + document + review statuses)
  if (isDataLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 ">
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              <span className="text-heading-2 font-heading-2 text-default-font">
                Loading Invoice Data...
              </span>
              <span className="text-body font-body text-subtext-color">
                Please wait while we fetch permissions, document details, and
                review statuses
              </span>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Show error if data loading failed
  if (reviewDataError) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 ">
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <IconWithBackground
                variant="error"
                icon={<FeatherAlertTriangle />}
              />
              <span className="text-heading-2 font-heading-2 text-default-font">
                Failed to Load Data
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                {reviewDataError.message ||
                  "Failed to load invoice data. Please try again."}
              </span>
              <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Check if invoiceJson data is available
  if (!invoiceJson) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 ">
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <IconWithBackground
                variant="error"
                icon={<FeatherAlertTriangle />}
              />
              <span className="text-heading-2 font-heading-2 text-default-font">
                No Document Data Available
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                The document data could not be loaded. Please go back and try
                again.
              </span>
              <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full flex-wrap items-start justify-between border-b border-solid border-neutral-border px-6 py-6">
          <div className="flex items-center gap-2">
            <Breadcrumbs>
              <Breadcrumbs.Item onClick={handleGoBack}>
                INVOICES
              </Breadcrumbs.Item>
              <Breadcrumbs.Divider />
              <Breadcrumbs.Item>
                {getFieldValue(invoiceJson?.invoice_number, "Document")}
              </Breadcrumbs.Item>
              <Breadcrumbs.Divider />
              <Breadcrumbs.Item active={true}>Review Items</Breadcrumbs.Item>
            </Breadcrumbs>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-save status indicator */}
            <div className="flex items-center gap-2 text-sm">
              {isAutoSaving && (
                <div className="flex items-center gap-1 text-blue-600">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span>Saving...</span>
                </div>
              )}
              {!isAutoSaving && lastSaved && !saveError && (
                <div className="flex items-center gap-1 text-green-600">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Saved {lastSaved.toLocaleTimeString()}</span>
                </div>
              )}
              {saveError && (
                <div className="flex items-center gap-2 text-red-600">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  <span>Save failed</span>
                  <button
                    onClick={() =>
                      invoiceJson && debouncedSave(invoiceJson, true)
                    }
                    className="text-xs underline hover:no-underline"
                    disabled={isAutoSaving}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
            <Button variant="neutral-secondary" onClick={handleGoBack}>
              Cancel
            </Button>
            {!isPostedToZoho && (
              <Button
                variant="brand-primary"
                onClick={saveAndGoBack}
                disabled={
                  isDataLoading ||
                  !canSaveAndGoBack ||
                  isCheckingReview ||
                  isReviewPending ||
                  isRecomputing
                }
              >
                {isRecomputing ? "Re-processing…" : "Save & Go Back"}
              </Button>
            )}
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col items-start min-h-0">
          {/* Display validation errors with ErrorBanner component */}
          {Object.keys(errors).length > 0 && (
            <div className="w-full px-6 pt-6 pb-4">
              <ErrorBanner
                errors={errors}
                title="Please fix the following errors:"
                autoScrollOnMount={true}
                context="invoice"
              />
            </div>
          )}

          {/* Closed month banner - show when document is in a closed month */}
          {isMonthClosed === true && (
            <div className="w-full px-6 pt-4 pb-2">
              <ClosedMonthBanner entityLabel="Invoice" />
            </div>
          )}

          {/* Written-off banner: show when invoice is written off, with Cancel write off */}
          {isWrittenOff && (
            <div className="w-full px-6 pt-4 pb-2">
              <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-4">
                <div className="flex w-full items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FeatherAlertCircle className="h-5 w-5 text-neutral-600 flex-none" />
                    <span className="text-body-bold font-body-bold text-neutral-700">
                      This invoice has been written off
                    </span>
                  </div>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={handleCancelWriteOff}
                    disabled={cancelWriteOffLoading || !canCancelWriteOff}
                    loading={cancelWriteOffLoading}
                  >
                    Cancel write off
                  </Button>
                </div>
                <div className="flex flex-col items-start gap-1 pl-7">
                  <span className="text-body font-body text-neutral-700">
                    Do you want to cancel the write off?
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Review Status Banner - Show at the top, before AI Detection Overview */}
          {review &&
            isReviewStatusBannerVisibleForUser(
              review,
              reviewBannerCurrentUserId,
            ) &&
            (review.status !== "approved" || showApprovedBanner) && (
              <div className="w-full px-6 pt-4 pb-4">
                <ReviewStatusBanner
                  review={review}
                  moduleCode={MODULE_CODES.INVOICE}
                  currentUserId={reviewBannerCurrentUserId}
                  onResubmit={() => {
                    if (review.status === "rejected") {
                      setIsReviewModalOpen(true);
                    }
                  }}
                />
              </div>
            )}

          {/* AI Validation Overview - Only show if not read-only, not written off, data available, customer matched, review not pending, and not manual */}
          {!isDocumentReadOnly &&
            !isReviewPending &&
            !isWrittenOff &&
            !isManualSource &&
            !isRecomputing &&
            !pendingCustomerRerun &&
            invoiceJson &&
            invoiceDocument &&
            customerMatched && (
              <AIDetectionOverviewInvoices
                invoiceData={invoiceJson}
                invoiceDocument={invoiceDocument}
                onValidationComplete={handleValidationComplete}
                onLoadingChange={setAiDetectionLoading}
                invoiceNumberEdited={invoiceNumberEdited}
              />
            )}

          {/* Customer Verification Section - hidden while a review is pending */}
          {!customerMatched && !isDocumentReadOnly && !isReviewPending && (
            <div className="flex w-full items-start justify-between border-b border-solid border-error-200 px-6 py-6 bg-gradient-to-r from-error-50 to-error-100">
              <div className="flex items-start gap-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-500">
                    <span className="text-body-bold font-body-bold text-white">
                      !
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Customer Verification Required
                    </span>
                    <Badge variant="error" icon={<FeatherAlertTriangle />}>
                      Customer Details Not Matched
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                    <span className="text-body font-body text-default-font">
                      Customer details must be verified before AI processing
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                    <span className="text-body font-body text-default-font">
                      Please search existing customers or add a new one
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex min-w-0 w-full items-start gap-6 px-6 py-6 pb-0">
            <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex min-w-0 w-full items-start gap-6">
                {/* PDF Viewer - manual: show attachments or full-width details; zoho/tally: no viewer (full-width details) */}
                {isManualSource ? (
                  attachmentUrls.length > 0 ? (
                    <div className="flex w-1/2 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 p-6">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Attachments
                      </span>
                      <div className="flex w-full flex-col gap-2">
                        {attachmentUrls.map((att, index) => (
                          <div
                            key={index}
                            className="flex w-full items-center gap-3 rounded-md bg-default-background px-3 py-3 border border-solid border-neutral-200"
                          >
                            <FeatherFile className="h-5 w-5 flex-none text-neutral-600" />
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="text-body-bold font-body-bold text-default-font truncate">
                                {att.filename}
                              </span>
                              {att.file_size != null && (
                                <span className="text-caption font-caption text-subtext-color">
                                  {(att.file_size / 1024).toFixed(2)} KB
                                </span>
                              )}
                            </div>
                            <IconButton
                              variant="neutral-tertiary"
                              size="small"
                              icon={<FeatherDownload />}
                              onClick={() =>
                                window.open(att.signed_url, "_blank")
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : /* No attachments: full-width for details, no empty left column */
                  null
                ) : isZohoSource ||
                  isTallySource /* Zoho/Tally: no document viewer, full-width details */ ? null : signedUrl ||
                  documentId ? (
                  <DocumentViewer {...documentViewerProps} />
                ) : (
                  <div className="flex w-1/2 items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 p-8">
                    <span className="text-body font-body text-subtext-color">
                      Loading PDF...
                    </span>
                  </div>
                )}

                {/* Invoice Details - full width when manual with no attachments or zoho/tally (no viewer). min-w-0 allows shrinking so line items table fits viewport. */}
                <div
                  className={`flex min-w-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm ${
                    (isManualSource && attachmentUrls.length === 0) ||
                    isZohoSource ||
                    isTallySource
                      ? "w-full"
                      : "w-1/2"
                  }`}
                >
                  <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-1 pt-1 pb-2.5">
                    <span className="text-heading-2 font-heading-2 text-default-font">
                      Invoice Details
                    </span>
                    {isManualSource && <Badge variant="neutral">Manual</Badge>}
                    {isZohoSource && <Badge variant="neutral">Zoho</Badge>}
                    {isTallySource && <Badge variant="neutral">Tally</Badge>}
                    {/* {isDocumentReadOnly ? (
                        <Badge variant="error">{documentStatusLabel}</Badge>
                      ) : (
                        <Badge variant="warning">Pending Review</Badge>
                      )} */}
                  </div>
                  <div className="flex w-full flex-col items-start gap-6">
                    {/* Customer & Service Details */}
                    <div
                      className={`flex w-full flex-col items-start gap-3 ${
                        customerMatched || isReviewPending
                          ? "border-b border-solid border-neutral-100 pb-4"
                          : "rounded-md border border-solid border-error-300 bg-error-100 px-4 py-4 bg-opacity-10"
                      }`}
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body-bold font-body-bold text-subtext-color">
                          Customer &amp; Service Details
                        </span>
                        <div className="flex items-center gap-2">
                          {customerMatched && (
                            <Badge variant="success" icon={<FeatherCheck />}>
                              Matched Customer
                            </Badge>
                          )}
                          <Button
                            size="large"
                            icon={<FeatherUserPen />}
                            onClick={handleSearchCustomerModalOpen}
                            disabled={
                              isDocumentReadOnly || shouldDisableEditableFields
                            }
                          >
                            {customerMatched ? "Change Customer" : "Search"}
                          </Button>
                        </div>
                      </div>
                      <div className="w-full items-start gap-4 grid grid-cols-3">
                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                          <span className="text-body font-body text-subtext-color">
                            Customer Name
                          </span>
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isSearchCustomerModalOpen}
                            oldText={fbacHd("customer_name")?.oldText ?? ""}
                            newText={shortenTrackedPartyChangeNewValue(
                              fbacHd("customer_name")?.newText ?? "",
                            )}
                          >
                            <span
                              className={`text-body-bold font-body-bold ${
                                customerMatched || isReviewPending
                                  ? "text-default-font"
                                  : "text-error-700"
                              }`}
                            >
                              {invoiceJson?.customer_id !== ""
                                ? invoiceJson?.customer_name
                                : "No Matched Customer"}
                            </span>
                          </ReviewPendingInlineValue>
                          {invoiceDocument?.customer_match_method &&
                            invoiceDocument.customer_match_details
                              ?.zoho_customer_id ===
                              invoiceJson?.customer_id && (
                              <SubframeCore.Tooltip.Provider>
                                <SubframeCore.Tooltip.Root>
                                  <SubframeCore.Tooltip.Trigger asChild={true}>
                                    <span className="cursor-pointer mt-0.5">
                                      <FeatherInfo className="h-3 w-3 text-subtext-color" />
                                    </span>
                                  </SubframeCore.Tooltip.Trigger>
                                  <SubframeCore.Tooltip.Portal>
                                    <SubframeCore.Tooltip.Content
                                      side="top"
                                      align="start"
                                      sideOffset={4}
                                      asChild={true}
                                    >
                                      <Tooltip>
                                        {invoiceDocument.customer_match_method ===
                                        "gstin_exact" ? (
                                          <span>Matched by GSTIN (exact)</span>
                                        ) : (
                                          <div className="flex flex-col gap-0.5">
                                            <span>
                                              Matched by multi-field (score:{" "}
                                              {Math.round(
                                                (invoiceDocument
                                                  .customer_match_details
                                                  ?.consolidated_score ?? 0) *
                                                  100,
                                              )}
                                              %)
                                            </span>
                                            {invoiceDocument
                                              .customer_match_details
                                              ?.field_scores && (
                                              <div className="flex flex-col gap-0.5 mt-1 border-t border-white/20 pt-1">
                                                {invoiceDocument
                                                  .customer_match_details
                                                  .field_scores.name !=
                                                  null && (
                                                  <span>
                                                    Name:{" "}
                                                    {Math.round(
                                                      invoiceDocument
                                                        .customer_match_details
                                                        .field_scores.name *
                                                        100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                                {invoiceDocument
                                                  .customer_match_details
                                                  .field_scores.gstin !=
                                                  null && (
                                                  <span>
                                                    GSTIN:{" "}
                                                    {Math.round(
                                                      invoiceDocument
                                                        .customer_match_details
                                                        .field_scores.gstin *
                                                        100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                                {invoiceDocument
                                                  .customer_match_details
                                                  .field_scores.pan != null && (
                                                  <span>
                                                    PAN:{" "}
                                                    {Math.round(
                                                      invoiceDocument
                                                        .customer_match_details
                                                        .field_scores.pan * 100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                                {invoiceDocument
                                                  .customer_match_details
                                                  .field_scores.address !=
                                                  null && (
                                                  <span>
                                                    Address:{" "}
                                                    {Math.round(
                                                      invoiceDocument
                                                        .customer_match_details
                                                        .field_scores.address *
                                                        100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </Tooltip>
                                    </SubframeCore.Tooltip.Content>
                                  </SubframeCore.Tooltip.Portal>
                                </SubframeCore.Tooltip.Root>
                              </SubframeCore.Tooltip.Provider>
                            )}
                        </div>

                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                          <span className="text-body font-body text-subtext-color">
                            Customer Address
                          </span>
                          <span
                            className={`text-body-bold font-body-bold ${
                              customerMatched || isReviewPending
                                ? "text-default-font"
                                : "text-error-700"
                            }`}
                          >
                            {invoiceJson?.customer_address ||
                              "No Customer Address"}
                          </span>
                        </div>
                      </div>
                      {!customerMatched && !isReviewPending && (
                        <div className="flex w-full items-start gap-4 rounded-md bg-error-600 px-4 py-4">
                          <IconWithBackground
                            variant="error"
                            size="medium"
                            icon={<FeatherAlertOctagon />}
                          />
                          <div className="flex flex-col items-start gap-1 grow">
                            <span className="text-heading-3 font-heading-3 text-white">
                              Critical: Unmatched Customer Details
                            </span>
                            <span className="text-caption font-caption text-neutral-100">
                              We detected{" "}
                              {invoiceJson?.customer_name?.trim() ||
                                "Unknown customer"}{" "}
                              but couldn&#39;t find a match in your records.
                              Please search above or add as a new customer.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Basic Information */}
                    <BasicInfoEditableField
                      initialData={basicInfo}
                      optionsData={basicInfo.optionsData}
                      onSave={handleBasicInfoSave}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      showExchangeRate={showExchangeRateField}
                      exchangeRateRequired={exchangeRateRequired}
                      exchangeRateLoading={exchangeRateLoading}
                      accountingBackend={accountingBackend}
                      exchangeRateError={errors.exchange_rate}
                      fieldDiffs={{
                        ...(fbacHd("invoice_number")
                          ? { invoice_number: fbacHd("invoice_number")! }
                          : {}),
                        ...(fbacHd("currency_symbol")
                          ? { currency_symbol: fbacHd("currency_symbol")! }
                          : {}),
                        ...(fbacHd("exchange_rate")
                          ? { exchange_rate: fbacHd("exchange_rate")! }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />

                    {/* Dates */}
                    <DatesEditableField
                      initialData={datesInfo}
                      onSave={handleDatesInfoSave}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      fieldDiffs={{
                        ...(fbacHd("date") ? { date: fbacHd("date")! } : {}),
                        ...(fbacHd("due_date")
                          ? { due_date: fbacHd("due_date")! }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />

                    {showPreGstNotice && (
                      <GstPreRegistrationNotice
                        documentDate={
                          invoiceJson?.date || fetchedInvoiceJson?.date || ""
                        }
                        documentLabel="invoice"
                      />
                    )}

                    {/* Tax Information */}
                    {/* ZOUR-65: which GST registration issues this invoice.
                        It decides our GSTIN on the document and the source of
                        supply, so it sits with the tax block. */}
                    {/* No border box and no padding — same change as the bill
                        review page (2026-09-15): the picker sits in the column
                        with the fields around it and the parent supplies the
                        spacing. */}
                    {hasMultipleLocations && (
                      <div className="flex w-full flex-col gap-4">
                        <LocationPicker
                          locations={activeLocations}
                          value={invoiceJson?.location_id ?? null}
                          /*
                           * MANUAL + POSTED = READ-ONLY. Twin of the bill review
                           * page, same reason: a manual document has no
                           * processing job to re-run, so moving it between
                           * registrations would leave the line tax family as it
                           * was while the place of supply moved. Uploaded
                           * invoices keep the editable picker.
                           */
                          locked={
                            isManualSource &&
                            (isPostedToZoho || !!invoiceDoc?.zoho_invoiceid)
                          }
                          onChange={(locationId) => {
                            // ZOUR-65 R17: an invoice is where WE supply FROM,
                            // so the source of supply is the new registration's
                            // state. See the bill twin — same reason, other end
                            // of the pair.
                            const picked = activeLocations.find(
                              (candidate) =>
                                candidate.zoho_location_id === locationId
                            );

                            updateInvoiceJson({
                              location_id: locationId,
                              ...(picked?.state_code
                                ? { source_of_supply: picked.state_code }
                                : {}),
                            });
                          }}
                        />
                      </div>
                    )}

                    <TaxInfoEditableField
                      initialData={taxInfo}
                      isGstEnabled={isGstEnabled}
                      optionsData={taxInfo.optionsData}
                      onSave={handleTaxInfoSave}
                      lineItems={lineItems}
                      currencySymbol={invoiceJson?.currency_symbol || "₹"}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      disableCustomerGstin={true}
                      fieldDiffs={{
                        ...(fbacHd("vendor_gstin")
                          ? { vendor_gstin: fbacHd("vendor_gstin")! }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />

                    {/* Document Metadata - hide for manual source */}
                    {!isManualSource && (
                      <DocumentMetadataEditableField
                        initialData={documentMetadata}
                        optionsData={documentMetadata.optionsData}
                        onSave={handleDocumentMetadataSave}
                        disabled={
                          isDocumentReadOnly || shouldDisableEditableFields
                        }
                      />
                    )}

                    {/* Amount Summary */}
                    <AmountSummaryEditableField
                      initialData={amountSummary}
                      isGstEnabled={isGstEnabled}
                      optionsData={amountSummary.optionsData}
                      onSave={handleAmountSummarySave}
                      lineItems={lineItems}
                      currencySymbol={invoiceJson?.currency_symbol || "₹"}
                      currentDiscount={discount}
                      onDiscountChange={handleDiscountChange}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      tcsTaxId={invoiceJson?.tcs_tax_id ?? null}
                      tcsTaxName={invoiceJson?.tcs_tax_name ?? null}
                      tcsTaxPercentage={invoiceJson?.tcs_tax_percentage}
                      onTcsChange={(value) => {
                        setUserHasChangedTcs(true);
                        if (value === null) {
                          updateInvoiceJson({
                            tcs_tax_id: "",
                            tcs_tax_name: "",
                            tcs_tax_percentage: undefined,
                            tcs_nature_of_collection: "",
                          });
                        } else {
                          updateInvoiceJson({
                            tcs_tax_id: value.tcs_tax_id,
                            tcs_tax_name: value.tcs_tax_name,
                            tcs_tax_percentage: value.tcs_tax_percentage,
                            tcs_nature_of_collection:
                              value.tcs_nature_of_collection ?? "",
                          });
                        }
                      }}
                      tcsRecommendation={tcsRecommendation}
                      userHasChangedTcs={userHasChangedTcs}
                      amountFieldDiffs={{
                        ...(fbacHd("sub_total")
                          ? { sub_total: fbacHd("sub_total")! }
                          : {}),
                        ...(fbacHd("tax_total")
                          ? { tax_total: fbacHd("tax_total")! }
                          : {}),
                        ...(fbacHd("total") ? { total: fbacHd("total")! } : {}),
                        ...(fbacHd("tcs_tax_name")
                          ? { tcs_tax_name: fbacHd("tcs_tax_name")! }
                          : {}),
                        ...(fbacHd("tcs_tax_percentage")
                          ? {
                              tcs_tax_percentage: fbacHd("tcs_tax_percentage")!,
                            }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />
                  </div>
                </div>
              </div>

              {/* Line Items Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-heading-2 font-heading-2 text-default-font">
                      Detected Line Items
                    </span>
                    <Badge variant="success" icon={<FeatherCheck />}>
                      {lineItems.length} Items Detected
                    </Badge>
                  </div>
                  <Button
                    variant="neutral-primary"
                    size="small"
                    icon={<FeatherPlus />}
                    onClick={handleAddLineItem}
                    disabled={
                      aiDetectionLoading ||
                      isDocumentReadOnly ||
                      shouldDisableEditableFields
                    }
                  >
                    Add line item
                  </Button>
                </div>

                {/* Line Items Table - equal columns, truncation + hover tooltips. Non-manual: no min-width + shrink so table fits viewport. */}
                <div className="flex w-full min-w-0 flex-col items-start rounded-md border border-solid border-neutral-border overflow-hidden">
                  <div className="flex min-w-0 items-center gap-2 sm:gap-4 border-b border-solid border-neutral-border bg-default-background px-2 sm:px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                    {/* NEW HEADER (2026-06-26, invoice parity with bills):
                        Description | Debit A/c | Credit A/c | HSN | Qty | Rate | GST | Amount | Actions.
                        AI Suggestion + Confidence columns dropped; Debit/Credit pulled up after
                        Description; numeric columns fixed-width so the text columns get room.
                        Credit A/c is the AI-classified income GL (the inline picker column). */}
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Description"
                      >
                        Description
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Debit A/c"
                      >
                        Debit A/c
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Credit A/c"
                      >
                        Credit A/c
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="HSN Code"
                      >
                        HSN Code
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Qty"
                      >
                        Qty
                      </span>
                    </div>
                    <div className="flex w-28 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Rate"
                      >
                        Rate
                      </span>
                    </div>
                    {isGstEnabled && (
                      <div className="flex w-20 shrink-0 items-start overflow-hidden">
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="GST"
                        >
                          GST
                        </span>
                      </div>
                    )}
                    <div className="flex w-28 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Amount"
                      >
                        Amount
                      </span>
                    </div>
                    <div className="flex w-[90px] shrink-0 items-start">
                      <span className="text-body font-body text-default-font truncate">
                        Actions
                      </span>
                    </div>
                  </div>

                  {/* Line Item Rows */}
                  {lineItems.map((item, index) => {
                    const gstLineLabel = formatDetectedLineItemGstDisplay(item);
                    const lineNo = index + 1;
                    const ld = (fieldKey: string) =>
                      fbacLineDiff(lineNo, fieldKey);
                    const lineAmountDiff =
                      ld("item_total_inclusive_of_tax") || ld("item_total");
                    const accountNameDiff = ld("account_name");
                    return (
                      <div
                        key={`${item.name}-${index}`}
                        className="flex min-w-0 items-start gap-2 sm:gap-4 border-b border-solid border-neutral-100 bg-white px-2 sm:px-4 py-3 w-full"
                      >
                        <div
                          className={`flex min-w-0 flex-1 flex-col items-start gap-0 overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("description")?.oldText ?? ""}
                            newText={ld("description")?.newText ?? ""}
                          >
                            <span
                              className="text-body-bold font-body-bold text-default-font truncate block w-full"
                              title={item.description || "—"}
                            >
                              {item.description}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* Debit A/c (fixed for invoices: Accounts Receivable) */}
                        <div
                          className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          <span
                            className="text-caption font-caption text-default-font truncate block w-full"
                            title="Account Receivable"
                          >
                            Account Receivable
                          </span>
                        </div>
                        {/* Credit A/c (AI-classified income GL — inline picker) */}
                        <div
                          className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          {inlineGlPickerActive && lineNeedsGlPick(index) ? (
                            // LLM line, not yet picked: empty AI-suggestion box.
                            <InlineGlAccountPicker
                              open={openInlineGlIndex === index}
                              onOpenChange={(o) =>
                                setOpenInlineGlIndex(o ? index : null)
                              }
                              suggestions={inlineGlSuggestionsForLine(index)}
                              onSelect={(s) => handleInlineGlSelect(index, s)}
                            />
                          ) : (
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={getAccountNameLeaf(
                                accountNameDiff?.oldText ?? "",
                              )}
                              newText={getAccountNameLeaf(
                                accountNameDiff?.newText ?? "",
                              )}
                              layout="stacked"
                              oldValueClassName="line-through opacity-70 text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                              newValueClassName="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                            >
                              <div className="flex w-full flex-col">
                                <span
                                  className="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                                  title={
                                    getAccountNameLeaf(item.account_name) || "—"
                                  }
                                >
                                  {getAccountNameLeaf(item.account_name)}
                                </span>
                                {/* Source layer (compact) for non-LLM lines:
                                    "Source: kNN · 69%" + info-icon hover tooltip
                                    with reason. The % is the top candidate's
                                    confidence (same figure the picker shows);
                                    hidden when absent. */}
                                {(() => {
                                  const meta = inlineGlBadgeMeta(index, item);
                                  if (!meta) return null;
                                  const pct =
                                    meta.confidence != null
                                      ? `${Math.round(meta.confidence * 100)}%`
                                      : null;
                                  // Info icon (hover = reason). Kept in the same
                                  // nowrap unit as the value so it never wraps to
                                  // a line by itself in the narrow A/c cell.
                                  const infoIcon = meta.reason ? (
                                    <SubframeCore.Tooltip.Provider>
                                      <SubframeCore.Tooltip.Root>
                                        <SubframeCore.Tooltip.Trigger
                                          asChild={true}
                                        >
                                          <span className="ml-1 inline-flex cursor-pointer align-middle leading-none">
                                            <FeatherInfo className="h-3 w-3 text-subtext-color" />
                                          </span>
                                        </SubframeCore.Tooltip.Trigger>
                                        <SubframeCore.Tooltip.Portal>
                                          <SubframeCore.Tooltip.Content
                                            side="top"
                                            align="start"
                                            sideOffset={4}
                                            asChild={true}
                                          >
                                            <Tooltip>{meta.reason}</Tooltip>
                                          </SubframeCore.Tooltip.Content>
                                        </SubframeCore.Tooltip.Portal>
                                      </SubframeCore.Tooltip.Root>
                                    </SubframeCore.Tooltip.Provider>
                                  ) : null;
                                  return (
                                    <span className="mt-0.5 block leading-snug text-caption font-caption-bold text-subtext-color">
                                      Source: {meta.label}
                                      {pct != null ? (
                                        <>
                                          {" "}
                                          <span className="whitespace-nowrap">
                                            · {pct}
                                            {infoIcon}
                                          </span>
                                        </>
                                      ) : (
                                        infoIcon
                                      )}
                                    </span>
                                  );
                                })()}
                              </div>
                            </ReviewPendingInlineValue>
                          )}
                        </div>
                        <div
                          className={`flex min-w-0 flex-1 flex-col items-start gap-0 overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("hsn_or_sac")?.oldText ?? ""}
                            newText={ld("hsn_or_sac")?.newText ?? ""}
                          >
                            <span
                              className="text-body font-body text-default-font truncate block w-full"
                              title={item.hsn_or_sac || "N/A"}
                            >
                              {item.hsn_or_sac || "N/A"}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        <div className="flex w-14 shrink-0 flex-col items-start gap-0 overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("quantity")?.oldText ?? ""}
                            newText={ld("quantity")?.newText ?? ""}
                          >
                            <span
                              className="text-body font-body text-default-font truncate block w-full"
                              title={String(item.quantity)}
                            >
                              {item.quantity}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        <div className="flex w-28 shrink-0 flex-col items-start gap-0 overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("rate")?.oldText ?? ""}
                            newText={ld("rate")?.newText ?? ""}
                          >
                            <span
                              className="text-body font-body text-default-font truncate block w-full"
                              title={`${invoiceJson?.currency_symbol || "₹"}${formatAmount(item.rate, "0.00")}`}
                            >
                              {invoiceJson?.currency_symbol || "₹"}
                              {formatAmount(item.rate, "0.00")}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {isGstEnabled && (
                          <div className="flex w-20 shrink-0 flex-col items-start gap-0.5 overflow-hidden">
                            {isReviewPending ? (
                              <ReviewPendingInlineValue
                                pendingReviewDiffActive={isReviewPending}
                                isEditing={isEditLineItemModalOpen}
                                oldText={ld("tax_name")?.oldText ?? ""}
                                newText={ld("tax_name")?.newText ?? ""}
                              >
                                <span
                                  className="text-body font-body text-default-font truncate block w-full"
                                  title={gstLineLabel}
                                >
                                  {gstLineLabel}
                                </span>
                              </ReviewPendingInlineValue>
                            ) : (
                              <div className="flex min-w-0 flex-col items-start gap-0.5">
                                <span
                                  className="text-body font-body text-default-font truncate block w-full"
                                  title={gstLineLabel}
                                >
                                  {gstLineLabel}
                                </span>
                                {Number(item.tax_amount ?? 0) !== 0 && (
                                  <span className="text-caption font-caption text-default-font">
                                    {formatAmount(item.tax_amount, "0.00")}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* GST recommendation hint. Read-only: surfaces ONLY the learned
                                (kNN) suggestion inline — document-sourced rates are already shown
                                in the tax field, so they add no signal. The review flag (⚠) still
                                shows for any source. */}
                            {(() => {
                              const lineGst =
                                validationData?.validation_results?.gst_classification?.details?.lines?.find(
                                  (l: any) => l.index === index,
                                );
                              if (!lineGst) return null;
                              const isKnn = lineGst.source_layer === "knn";
                              // Nothing worth showing for plain document-sourced lines.
                              if (!isKnn && !lineGst.needs_manual_review)
                                return null;
                              const tooltipText = [
                                lineGst.recommended_gst_rate != null &&
                                  `GST: ${lineGst.recommended_gst_rate}%${lineGst.recommended_gst_class ? ` (${lineGst.recommended_gst_class})` : ""}`,
                                isKnn &&
                                  lineGst.confidence != null &&
                                  `Confidence: ${Math.round(lineGst.confidence * 100)}%`,
                                lineGst.reasoning,
                              ]
                                .filter(Boolean)
                                .join("\n");
                              return (
                                <div className="flex items-center gap-1">
                                  {isKnn &&
                                    lineGst.recommended_gst_rate != null && (
                                      <span className="shrink-0 text-[10px] leading-none font-caption text-subtext-color">
                                        kNN {lineGst.recommended_gst_rate}%
                                      </span>
                                    )}
                                  {(isKnn || lineGst.needs_manual_review) &&
                                    tooltipText && (
                                      <SubframeCore.Tooltip.Provider>
                                        <SubframeCore.Tooltip.Root>
                                          <SubframeCore.Tooltip.Trigger
                                            asChild={true}
                                          >
                                            <span className="shrink-0 cursor-default text-subtext-color">
                                              <FeatherInfo className="h-2.5 w-2.5" />
                                            </span>
                                          </SubframeCore.Tooltip.Trigger>
                                          <SubframeCore.Tooltip.Portal>
                                            <SubframeCore.Tooltip.Content
                                              side="top"
                                              align="start"
                                              sideOffset={4}
                                              asChild={true}
                                            >
                                              <Tooltip className="max-w-[200px] whitespace-pre-wrap">
                                                {tooltipText}
                                              </Tooltip>
                                            </SubframeCore.Tooltip.Content>
                                          </SubframeCore.Tooltip.Portal>
                                        </SubframeCore.Tooltip.Root>
                                      </SubframeCore.Tooltip.Provider>
                                    )}
                                  {lineGst.needs_manual_review && (
                                    <span
                                      title="GST engine suggests reviewing this rate"
                                      className="shrink-0 text-[10px] leading-none text-warning-500"
                                    >
                                      ⚠
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        <div className="flex w-28 shrink-0 flex-col items-start gap-0 overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={lineAmountDiff?.oldText ?? ""}
                            newText={lineAmountDiff?.newText ?? ""}
                          >
                            <span
                              className="text-body-bold font-body-bold text-default-font truncate block w-full"
                              title={`${invoiceJson?.currency_symbol || "₹"}${formatAmount(
                                isGstEnabled
                                  ? item.rate * item.quantity +
                                      (item.rate *
                                        item.quantity *
                                        (item.tax_percentage || 0)) /
                                        100
                                  : item.rate * item.quantity,
                                "0.00",
                              )}`}
                            >
                              {invoiceJson?.currency_symbol || "₹"}
                              {formatAmount(
                                isGstEnabled
                                  ? item.rate * item.quantity +
                                      (item.rate *
                                        item.quantity *
                                        (item.tax_percentage || 0)) /
                                        100
                                  : item.rate * item.quantity,
                                "0.00",
                              )}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* Old Debit A/c, Credit A/c, and Confidence cells removed:
                            Debit/Credit moved up after Description (Credit A/c is now the
                            inline picker), AI Suggestion + Confidence columns dropped. */}
                        <div className="flex w-[90px] shrink-0 items-center gap-1">
                          <IconButton
                            size="small"
                            icon={<FeatherX />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.stopPropagation();
                              handleDeleteLineItem(index);
                            }}
                            disabled={
                              isDataLoading ||
                              aiDetectionLoading ||
                              isDocumentReadOnly ||
                              shouldDisableEditableFields
                            }
                          />
                          <IconButton
                            size="small"
                            icon={<FeatherEdit2 />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.stopPropagation();
                              handleEditLineItem(index);
                            }}
                            disabled={
                              isDataLoading ||
                              aiDetectionLoading ||
                              !(isPostedToZoho
                                ? canUpdateAfterPush
                                : canEdit) ||
                              isDocumentReadOnly ||
                              isReviewPending
                            }
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Totals */}
                  <div className="flex flex-col items-end gap-3 border-t border-solid border-neutral-200 bg-default-background px-4 py-4 min-w-full">
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Subtotal
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {invoiceJson?.currency_symbol || "₹"}
                        {/* {lineItems
                            .reduce(
                              (sum, item) => sum + item.rate * item.quantity,
                              0
                            )
                            .toFixed(2)} */}
                        {formatAmount(
                          lineItems.reduce(
                            (sum, item) => sum + item.rate * item.quantity,
                            0,
                          ),
                          "0.00",
                        )}
                      </span>
                    </div>
                    {/* Show discount if exists */}
                    {discount !== 0 && (
                      <div className="flex w-full max-w-[384px] items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Discount/Adjustments
                        </span>
                        <span
                          className={`text-body-bold font-body-bold ${
                            discount < 0 ? "text-success-600" : "text-error-600"
                          }`}
                        >
                          {invoiceJson?.currency_symbol || "₹"}
                          {formatAmount(discount, "0.00")}
                        </span>
                      </div>
                    )}
                    {isGstEnabled &&
                      taxBreakdown.rows.map((row) => (
                        <div
                          key={row.label}
                          className="flex w-full max-w-[384px] items-center justify-between"
                        >
                          <span className="text-body font-body text-subtext-color">
                            {row.label}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {invoiceJson?.currency_symbol || "₹"}
                            {formatAmount(row.amount, "0.00")}
                          </span>
                        </div>
                      ))}
                    {(() => {
                      const subtotal = lineItems.reduce(
                        (sum, item) => sum + item.rate * item.quantity,
                        0,
                      );
                      const totalTax = isGstEnabled ? taxBreakdown.totalTax : 0;
                      const tcsPercent =
                        isGstEnabled &&
                        invoiceJson?.tcs_tax_id &&
                        invoiceJson.tcs_tax_id !== "" &&
                        invoiceJson?.tcs_tax_percentage != null
                          ? invoiceJson.tcs_tax_percentage
                          : 0;
                      const tcsAmount =
                        tcsPercent > 0
                          ? (subtotal + totalTax) * (tcsPercent / 100)
                          : 0;
                      return (
                        tcsAmount > 0 && (
                          <div className="flex w-full max-w-[384px] items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              TCS
                              {invoiceJson?.tcs_tax_name
                                ? ` (${invoiceJson.tcs_tax_name})`
                                : ""}
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {invoiceJson?.currency_symbol || "₹"}
                              {formatAmount(tcsAmount, "0.00")}
                            </span>
                          </div>
                        )
                      );
                    })()}
                    <div className="flex w-full max-w-[384px] items-center justify-between border-t border-solid border-neutral-border pt-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total Amount
                      </span>
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        {invoiceJson?.currency_symbol || "₹"}
                        {(() => {
                          const subtotal = lineItems.reduce(
                            (sum, item) => sum + item.rate * item.quantity,
                            0,
                          );
                          const totalTax = isGstEnabled
                            ? taxBreakdown.totalTax
                            : 0;
                          const tcsPercent =
                            isGstEnabled &&
                            invoiceJson?.tcs_tax_id &&
                            invoiceJson?.tcs_tax_percentage != null
                              ? invoiceJson.tcs_tax_percentage
                              : 0;
                          const tcsAmount =
                            tcsPercent > 0
                              ? (subtotal + totalTax) * (tcsPercent / 100)
                              : 0;
                          return formatAmount(
                            subtotal + discount + totalTax + tcsAmount,
                            "0.00",
                          );
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex w-full flex-col items-start gap-3 pt-2">
                  {/* Warning messages */}
                  <div className="flex flex-col gap-2">
                    {isDocumentReadOnly && (
                      <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-100 border border-solid border-neutral-200">
                        <FeatherAlertTriangle className="h-4 w-4 text-error-600" />
                        <span className="text-body font-body text-subtext-color">
                          This document is {documentStatusLabel?.toLowerCase()}{" "}
                          and cannot be modified
                        </span>
                      </div>
                    )}
                    {hasUnresolvedQueries && !isDocumentReadOnly && (
                      <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-warning-50 border border-solid border-warning-200">
                        <FeatherAlertTriangle className="h-4 w-4 text-warning-600" />
                        <span className="text-body font-body text-warning-700">
                          Please resolve {unresolvedCount} open{" "}
                          {unresolvedCount === 1 ? "query" : "queries"} before{" "}
                          {isPostedToZoho ? "updating" : "approving"} this
                          document
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Button
                        icon={<FeatherCheck />}
                        onClick={handleApproveClick}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          hasUnresolvedQueries ||
                          !canApprove ||
                          isReviewPending ||
                          aiDetectionLoading ||
                          isWrittenOff ||
                          exchangeRateLoading
                        }
                      >
                        {isApprovalLoading
                          ? isPostedToZoho
                            ? "Updating..."
                            : "Approving..."
                          : isPostedToZoho
                            ? "Update Invoice"
                            : "Approve"}
                      </Button>
                      {!canApprove && !isDataLoading && (
                        <Tooltip content="You do not have permission to perform this action">
                          <FeatherLock className="h-4 w-4 text-neutral-400" />
                        </Tooltip>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="brand-secondary"
                        icon={<FeatherMessageCircle />}
                        onClick={handleQueryRaiseModalOpen}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          (!canEdit && !canPushToZoho) ||
                          isReviewPending ||
                          isWrittenOff
                        }
                      >
                        Raise a Query
                      </Button>
                      {!canEdit && !canPushToZoho && !isDataLoading && (
                        <Tooltip content="You do not have permission to raise queries for this document">
                          <FeatherLock className="h-4 w-4 text-neutral-400" />
                        </Tooltip>
                      )}
                    </div>
                    {!isPostedToZoho && canReject && (
                      <Button
                        variant="destructive-secondary"
                        icon={<FeatherX />}
                        onClick={handleRejectInvoice}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          isReviewPending ||
                          aiDetectionLoading ||
                          isWrittenOff
                        }
                      >
                        Reject
                      </Button>
                    )}
                    {isPostedToZoho && canDelete && (
                      <Button
                        variant="destructive-secondary"
                        icon={<FeatherX />}
                        onClick={handleDeleteInvoice}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          isReviewPending ||
                          aiDetectionLoading ||
                          isWrittenOff
                        }
                      >
                        Delete
                      </Button>
                    )}
                    {!isPostedToZoho && canMarkAsDuplicate && (
                      <Button
                        variant="neutral-secondary"
                        icon={<FeatherCopy />}
                        onClick={handleMarkAsDuplicate}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          isReviewPending ||
                          aiDetectionLoading ||
                          isWrittenOff
                        }
                      >
                        Mark as Duplicate
                      </Button>
                    )}
                  </div>
                  {aiDetectionLoading && (
                    <div className="flex w-full items-center gap-2 px-4 py-2 rounded-md bg-warning-50 border border-solid border-warning-200">
                      <span className="text-body font-body text-warning-700">
                        AI detection is running. You cannot perform these
                        actions until it completes.
                      </span>
                    </div>
                  )}
                </div>

                {/* Activity Log and Queries */}
                <div className="flex w-full items-start gap-6">
                  <QueriesListForDocument
                    queries={queriesEntities}
                    loading={queriesLoading}
                    loadingMore={queriesLoadingMore}
                    hasMore={queriesHasMore}
                    onLoadMore={loadMoreQueries}
                    onQueryClick={handleQueryClick}
                  />

                  {/* Activity Log */}
                  {documentId && (
                    <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 w-1/2">
                      <ActivityLogComponent
                        key={activityLogRefreshKey}
                        feature="invoice"
                        featureId={documentId}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SearchCustomerModal
        open={isSearchCustomerModalOpen}
        onOpenChange={setIsSearchCustomerModalOpen}
        onPartySelect={(customer) => handleCustomerSelect(customer, true)} // true = manual selection
        id={documentId!}
        initialSearchTerm={invoiceJson?.customer_name}
        initialSelectedCustomerId={invoiceJson?.customer_id}
      />
      <CustomerRerunConfirmModal
        open={!!pendingCustomerRerun}
        loading={isRecomputing}
        error={rerunError}
        oldCustomerName={pendingCustomerRerun?.oldCustomerName ?? null}
        newCustomerName={pendingCustomerRerun?.customerName ?? null}
        onOpenChange={(open) => {
          if (!open && !isRecomputing) {
            setPendingCustomerRerun(null);
            setRerunError(null);
          }
        }}
        onConfirm={() => {
          if (!pendingCustomerRerun || isRecomputing) return;
          void triggerCustomerRerun(
            pendingCustomerRerun.customerId,
            pendingCustomerRerun.customerName,
          );
        }}
      />
      <QueryRaiseModal
        open={isQueryRaiseModalOpen}
        onOpenChange={setIsQueryRaiseModalOpen}
        sourceDoc={{
          type: "invoice",
          id: parseInt(documentId || "0"),
          filename: fileName || "document.pdf",
          amount: invoiceJson?.currency_symbol + invoiceJson?.total,
          viewUrl: signedUrl || "",
        }}
        onQueryRaised={refetchQueries}
      />
      <QueryDetailModal
        open={isQueryDetailModalOpen}
        onOpenChange={setIsQueryDetailModalOpen}
        queryId={selectedQueryId}
        onQueryResolved={refetchQueries}
        hideViewSourceButton={true}
      />

      {/* GL Classification Modal */}
      <GLClassificationModal
        open={isGLClassificationModalOpen}
        onOpenChange={setIsGLClassificationModalOpen}
        lineItems={glClassificationLineItems}
        onApply={handleGLClassificationApply}
        onSkip={handleGLClassificationSkip}
      />

      {/* Apply GL As Default Modal - shown on Approve when one or more line
          items still have the AI-suggested GL (account name not edited).
          Backdrop / escape dismissal is treated as "no further action": the
          user must click Approve again to continue. The explicit "Not now"
          and "Save as default suggestion" buttons drive the approval flow. */}
      <ApplyGLAsDefaultModal
        open={isApplyGLAsDefaultOpen}
        onOpenChange={(open) => {
          if (isApplyingGLDefaults) return; // lock during in-flight overrides
          setIsApplyGLAsDefaultOpen(open);
          if (!open) {
            setApplyGLAsDefaultItems([]);
          }
        }}
        documentType="invoice"
        principalName={invoiceJson?.customer_name}
        lineItems={applyGLAsDefaultItems}
        onNotNow={handleApplyGLAsDefaultSkip}
        onSaveAsDefault={handleApplyGLAsDefaultSave}
        isLoading={isApplyingGLDefaults}
      />

      {/* Edit Line Item Modal */}
      <EditLineItemForInvoiceComponent
        open={isEditLineItemModalOpen}
        onOpenChange={setIsEditLineItemModalOpen}
        editingLineItemData={editingLineItemData}
        originalAccountName={originalAccountName}
        originalAccountId={originalAccountId}
        editingLineItemIndex={editingLineItemIndex}
        customerName={invoiceJson?.customer_name}
        customerId={invoiceJson?.customer_id}
        taxes={taxes}
        taxesLoading={taxesLoading}
        isNewItem={editingLineItemIndex === -1}
        currencySymbol={invoiceJson?.currency_symbol || "₹"}
        gstTreatment={invoiceJson?.gst_treatment}
        onSave={handleSaveLineItem}
        onCancel={handleCancelEdit}
        onLineItemDataChange={handleLineItemDataChange}
        isGstEnabled={isGstEnabled}
        invoiceId={documentId}
        onGLOverrideNeedsReview={(glOverrideData) => {
          setPendingGLOverrideReview((prev) => {
            // Check if a GL override already exists for this line item
            const existingIndex = prev.findIndex(
              (gl) => gl.lineItemIndex === glOverrideData.lineItemIndex,
            );
            if (existingIndex >= 0) {
              // Update existing GL override
              const updated = [...prev];
              updated[existingIndex] = glOverrideData;
              return updated;
            } else {
              // Add new GL override
              return [...prev, glOverrideData];
            }
          });
        }}
      />

      {/* Update GL Dialog */}
      {/* {pendingGLUpdate && (
        <UpdateGLDialog
          open={isUpdateGLDialogOpen}
          onOpenChange={setIsUpdateGLDialogOpen}
          changedLineItemIndex={pendingGLUpdate.changedLineItemIndex}
          oldAccountName={pendingGLUpdate.oldAccountName}
          newAccountName={pendingGLUpdate.newAccountName}
          newAccountId={pendingGLUpdate.newAccountId}
          allLineItems={lineItems}
          currencySymbol={invoiceJson?.currency_symbol || "₹"}
          onApply={handleApplyGLToSelected}
          onSkip={handleSkipGLUpdate}
        />
      )} */}

      {/* Validation Progress Modal */}
      <Dialog
        open={isValidationProgressModalOpen}
        onOpenChange={setIsValidationProgressModalOpen}
      >
        <Dialog.Content className="min-w-[600px] max-w-[700px]">
          {/* Header */}
          <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-warning-500">
                <span className="text-body-bold font-body-bold text-white">
                  AI
                </span>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Validation Issues Detected
                </span>
                <span className="text-body font-body text-subtext-color">
                  Some validations failed. Do you want to continue?
                </span>
              </div>
            </div>
            <IconButton
              size="small"
              icon={<FeatherX />}
              onClick={handleCloseValidationModal}
            />
          </div>

          {/* Content */}
          <div className="flex w-full flex-col gap-6 p-6">
            {isApprovalLoading ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Approving Document...
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Please wait while we process your approval. This may take a
                    few moments.
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-warning-100">
                    <FeatherAlertTriangle className="h-4 w-4 text-warning-600" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Validation Warnings
                    </span>
                    <span className="text-body font-body text-subtext-color">
                      The following validations failed:
                    </span>
                  </div>
                </div>

                {validationData && (
                  <div className="flex flex-col gap-3 rounded-md border border-solid border-neutral-200 bg-neutral-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-body font-body text-default-font">
                        Current Progress:
                      </span>
                      <span className="text-body-bold font-body-bold text-warning-700">
                        {validationData.validation_summary
                          ?.passed_validations || 0}
                        /
                        {validationData.validation_summary?.total_validations ||
                          8}{" "}
                        Checks Passed
                      </span>
                    </div>

                    {failedValidationMessages.length > 0 && (
                      <div className="flex flex-col gap-2 mt-3">
                        <span className="text-body-bold font-body-bold text-error-700">
                          Failed Validations:
                        </span>
                        {failedValidationMessages.map((message, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                            <span className="text-caption font-caption text-error-700">
                              {message}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex w-full items-center justify-end gap-3">
                  <Button
                    variant="neutral-secondary"
                    onClick={handleCloseValidationModal}
                    disabled={isApprovalLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand-secondary"
                    onClick={handleContinueApproval}
                    disabled={isApprovalLoading}
                  >
                    {isApprovalLoading ? "Approving..." : "Continue Anyway"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog>

      {/* Closed month confirm modal - when non-Admin/CFO tries to save or approve in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={(open) => {
          setShowClosedMonthConfirmModal(open);
          if (!open) setPendingClosedMonthSource(null);
        }}
        entityLabel="Invoice"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          if (pendingClosedMonthSource === "approve") {
            setPendingReviewAction({
              action: () => {},
              actionCode: isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : ACTION_CODES.PUSH_TO_ZOHO,
              changeDetails: {
                closed_month: true,
                action_types: [
                  isPostedToZoho ? "update_after_push" : "push_to_zoho",
                  ACTION_TYPE_MONTH_CLOSE,
                ],
                field_changes: trackedChanges,
                raw_json: invoiceJson ?? undefined,
                original_json: originalInvoiceJson ?? undefined,
                validation_snapshot: buildValidationSnapshotForReview(
                  validationBaselineLineItemsRef.current,
                  invoiceJsonRef.current || invoiceJson,
                ),
              },
              disableSubmit: false,
            });
          } else {
            setPendingReviewAction({
              action: () => {},
              actionCode: isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : ACTION_CODES.UPDATE,
              changeDetails: {
                closed_month: true,
                action_types: [
                  isPostedToZoho ? "update_after_push" : "edit",
                  ACTION_TYPE_MONTH_CLOSE,
                ],
                field_changes: trackedChanges,
                raw_json: invoiceJson ?? undefined,
                original_json: originalInvoiceJson ?? undefined,
                validation_snapshot: buildValidationSnapshotForReview(
                  validationBaselineLineItemsRef.current,
                  invoiceJsonRef.current || invoiceJson,
                ),
              },
              disableSubmit: false,
            });
          }
          setPendingClosedMonthSource(null);
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Changes Modal */}
      {documentId && (
        <InvoiceBillReviewChangesModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.INVOICE}
          actionCode={
            pendingReviewAction?.actionCode ??
            (pendingGLOverrideReview.length > 0 &&
            needsReviewForOverrideGLPermanent
              ? ACTION_CODES.OVERRIDE_GL_PERMANENT
              : isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : needsReviewForPushToZoho
                  ? ACTION_CODES.PUSH_TO_ZOHO
                  : ACTION_CODES.UPDATE)
          }
          entityType="invoice"
          entityId={parseInt(documentId)}
          documentNumber={invoiceJson?.invoice_number}
          changeDetails={pendingReviewAction?.changeDetails}
          disableSubmit={pendingReviewAction?.disableSubmit}
          requiresReview={pendingReviewAction?.requiresReview ?? true}
          skipNavigation={true}
          onSuccess={async (comments) => {
            if (pendingReviewAction?.requiresReview === false) {
              const changeDetails = pendingReviewAction.changeDetails;
              setPendingReviewAction(null);
              setIsReviewModalOpen(false);
              if (!changeDetails || !comments?.trim()) {
                return;
              }
              await handleDocumentApproval({
                skipApplyGLPrompt: true,
                skipReviewChangesModal: true,
                approvalAudit: {
                  reason: comments.trim(),
                  change_details: changeDetails,
                },
              });
              return;
            }

            if (
              pendingReviewAction?.changeDetails?.action_types?.includes(
                "create",
              )
            ) {
              setPendingReviewAction(null);
              setIsReviewModalOpen(false);
              handleGoBack();
              return;
            }
            setTrackedChanges([]);
            setPendingGLOverrideReview([]);
            handleGoBack();
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setApiErrors(error);
          }}
        />
      )}
    </DefaultPageLayout>
  );
};

export default InvoiceReviewPage;
